import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  FaArrowDown,
  FaArrowUp,
  FaBoxes,
  FaCheckCircle,
  FaClipboardList,
  FaDownload,
  FaEdit,
  FaExclamationTriangle,
  FaExchangeAlt,
  FaFileImport,
  FaFilter,
  FaFilePdf,
  FaLightbulb,
  FaChartLine,
  FaPlus,
  FaSearch,
  FaSyncAlt,
  FaTimes,
  FaTrash,
  FaWarehouse,
  FaChartPie,
  FaRobot,
  FaHistory,
  FaSave,
  FaSpinner,
} from 'react-icons/fa';
import { read as readXLSX, utils as XLSXUtils, writeFile as writeXLSXFile } from '@redoper1/xlsx-js-style';
import '../styles/inventoryResponsive.css';
import { useAuth } from '../AuthContext';
import Modal from '../components/Modal';
import InventoryService from '../services/InventoryService';
import AIInsightsPanel from '../components/AIInsightsPanel';
import ProjectBankService from '../services/ProjectBankService';
import {
  INVENTORY_MANAGE_PERMISSION,
  INVENTORY_VIEW_PERMISSION,
  hasPermission,
} from '../utils/permissions';
import {
  DEFAULT_TABLE_PAGE_SIZE,
  TABLE_PAGE_SIZE_OPTIONS,
  paginateTableRows,
} from '../utils/tablePagination';

const MOVEMENT_TYPES = [
  { value: 'INBOUND', label: 'Entrada', icon: FaArrowDown },
  { value: 'OUTBOUND', label: 'Saída', icon: FaArrowUp },
  { value: 'TRANSFER', label: 'Transferência', icon: FaExchangeAlt },
];

const resolveErrorMessage = (error) => {
  if (!error) return 'Ocorreu um erro inesperado.';
  if (error.response?.data?.error) {
    return error.response.data.error;
  }
  if (typeof error.message === 'string') {
    return error.message;
  }
  return 'Ocorreu um erro inesperado.';
};

const formatCurrency = (value) => {
  const amount = Number(value || 0);
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(amount);
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(date);
};

const formatDate = (value) => {
  if (!value) return '-';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
  }).format(date);
};

const toInputDateValue = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
};


const IMPORT_REQUIRED_FIELDS = ['name', 'warehouse', 'quantity'];

const IMPORT_COLUMNS = [
  { key: 'name', label: 'Nome do item', required: true, instructions: 'Nome principal do produto/material em estoque.' },
  { key: 'sku', label: 'SKU', required: false, instructions: 'Código interno, referência ou SKU do item.' },
  { key: 'category', label: 'Categoria', required: false, instructions: 'Nome da categoria já cadastrada (opcional).' },
  { key: 'warehouse', label: 'Depósito', required: true, instructions: 'Nome ou código do depósito já cadastrado que receberá o saldo inicial.' },
  { key: 'unit', label: 'Unidade', required: false, instructions: 'Unidade de medida. Se vazio, será usada "un".' },
  { key: 'quantity', label: 'Quantidade inicial', required: true, instructions: 'Saldo inicial numérico a importar para o depósito informado.' },
  { key: 'costPrice', label: 'Custo de compra', required: false, instructions: 'Valor numérico do custo unitário (ex: 42.90).' },
  { key: 'salePrice', label: 'Preço de venda', required: false, instructions: 'Valor numérico do preço de venda (opcional).' },
  { key: 'minimumStock', label: 'Estoque mínimo', required: false, instructions: 'Quantidade mínima para alertas de reposição.' },
  { key: 'maximumStock', label: 'Estoque máximo', required: false, instructions: 'Quantidade máxima desejada para o item.' },
  { key: 'safetyStock', label: 'Estoque de segurança', required: false, instructions: 'Quantidade de segurança/reserva operacional.' },
  { key: 'expirationDate', label: 'Validade', required: false, instructions: 'Data no formato AAAA-MM-DD, quando aplicável.' },
  { key: 'trackBatch', label: 'Controla lote', required: false, instructions: 'Informe Sim ou Não.' },
  { key: 'trackExpiration', label: 'Controla validade', required: false, instructions: 'Informe Sim ou Não.' },
  { key: 'active', label: 'Ativo', required: false, instructions: 'Informe Sim ou Não. Se vazio, será considerado Sim.' },
  { key: 'description', label: 'Descrição', required: false, instructions: 'Detalhes complementares do item.' },
];

const getImportColumnLabel = (column) => `${column.required ? '* ' : ''}${column.label}`;
const NASH_HEADER_BG = '0B5ED7';
const NASH_HEADER_FG = 'FFFFFF';
const INVENTORY_TAB_STORAGE_KEY = 'inventory-active-tab';
const getWarehousePreferenceKey = (user) => `inventory-preferred-warehouse-${user?.id ?? user?.email ?? 'anonymous'}`;

const applyNashHeaderStyle = (worksheet, headers) => {
  headers.forEach((_, index) => {
    const cellAddress = XLSXUtils.encode_cell({ c: index, r: 0 });
    if (!worksheet[cellAddress]) return;
    worksheet[cellAddress].s = {
      fill: { fgColor: { rgb: NASH_HEADER_BG } },
      font: { bold: true, color: { rgb: NASH_HEADER_FG } },
      alignment: { horizontal: 'center', vertical: 'center' },
    };
  });
};

const normalizeText = (value) =>
  String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();

const normalizeHeader = (value) =>
  normalizeText(value)
    .replace(/^\*\s*/, '')
    .replace(/[^a-z0-9]+/g, '');

const resolveTextValue = (value) => {
  if (value === null || value === undefined) return '';
  return String(value).trim();
};

const parseImportNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const normalized = String(value)
    .trim()
    .replace(/\s/g, '')
    .replace(/\.(?=\d{3}(\D|$))/g, '')
    .replace(',', '.');
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const parseImportBoolean = (value, fallback = false) => {
  const normalized = normalizeText(value);
  if (!normalized) return fallback;
  if (['sim', 's', 'yes', 'y', 'true', '1', 'ativo'].includes(normalized)) return true;
  if (['nao', 'n', 'no', 'false', '0', 'inativo'].includes(normalized)) return false;
  return fallback;
};


const getAiInsightConfig = (text = '') => {
  const normalized = normalizeText(text);
  if (normalized.includes('risco') || normalized.includes('critico') || normalized.includes('ruptura') || normalized.includes('baixo')) {
    return { icon: FaExclamationTriangle, color: 'rose', label: 'Risco' };
  }
  if (normalized.includes('oportunidade') || normalized.includes('economia') || normalized.includes('excesso')) {
    return { icon: FaLightbulb, color: 'amber', label: 'Oportunidade' };
  }
  if (normalized.includes('acao') || normalized.includes('proxima') || normalized.includes('repor') || normalized.includes('comprar')) {
    return { icon: FaCheckCircle, color: 'emerald', label: 'Ação' };
  }
  return { icon: FaChartLine, color: 'indigo', label: 'Insight' };
};

const getAiInsightClasses = (color) => ({
  rose: 'border-rose-100 bg-rose-50 text-rose-700',
  amber: 'border-amber-100 bg-amber-50 text-amber-700',
  emerald: 'border-emerald-100 bg-emerald-50 text-emerald-700',
  indigo: 'border-indigo-100 bg-indigo-50 text-indigo-700',
}[color] || 'border-indigo-100 bg-indigo-50 text-indigo-700');

const parseAiInsights = (analysis = '') =>
  String(analysis || '')
    .split(/\n+/)
    .map((line) => line.replace(/^[-•*\d.)\s]+/, '').trim())
    .filter(Boolean);

const parseImportDate = (value) => {
  if (value === null || value === undefined || value === '') return '';
  if (typeof value === 'number') {
    const date = new Date(Math.round((value - 25569) * 86400 * 1000));
    return Number.isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 10);
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? '' : value.toISOString().slice(0, 10);
  }
  const raw = String(value).trim();
  const brazilianDate = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  const normalized = brazilianDate
    ? `${brazilianDate[3]}-${brazilianDate[2].padStart(2, '0')}-${brazilianDate[1].padStart(2, '0')}`
    : raw;
  const date = new Date(`${normalized}T00:00:00`);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 10);
};

const emptyItemForm = {
  id: null,
  name: '',
  sku: '',
  categoryId: '',
  warehouseId: '',
  unit: 'un',
  costPrice: '',
  salePrice: '',
  minimumStock: '',
  maximumStock: '',
  safetyStock: '',
  description: '',
  trackBatch: false,
  trackExpiration: false,
  expirationDate: '',
  active: true,
};

const emptyWarehouseForm = {
  id: null,
  name: '',
  code: '',
  manager: '',
  phone: '',
  email: '',
  address: '',
  notes: '',
};

const emptyCategoryForm = {
  id: null,
  name: '',
  description: '',
};

const emptyMovementForm = {
  itemId: '',
  type: 'INBOUND',
  quantity: '',
  originWarehouseId: '',
  targetWarehouseId: '',
  unitCost: '',
  occurredAt: new Date().toISOString().slice(0, 16),
  referenceCode: '',
  notes: '',
};

const normalizeCategory = (category) => {
  if (!category || typeof category !== 'object') {
    return { id: null, name: 'Sem categoria', description: '' };
  }
  return {
    id: category.id ?? category.categoryId ?? null,
    name: category.name ?? category.title ?? 'Sem categoria',
    description: category.description ?? category.notes ?? '',
  };
};

const normalizeWarehouse = (warehouse) => {
  if (!warehouse || typeof warehouse !== 'object') {
    return {
      id: null,
      name: 'Geral',
      code: '',
      manager: '',
      phone: '',
      email: '',
      address: '',
      notes: '',
    };
  }
  return {
    id: warehouse.id ?? warehouse.warehouseId ?? null,
    name: warehouse.name ?? warehouse.title ?? 'Geral',
    code: warehouse.code ?? warehouse.identifier ?? '',
    manager: warehouse.manager ?? warehouse.responsible ?? '',
    phone: warehouse.phone ?? warehouse.contactPhone ?? '',
    email: warehouse.email ?? warehouse.contactEmail ?? '',
    address: warehouse.address ?? warehouse.location ?? '',
    notes: warehouse.notes ?? warehouse.description ?? '',
  };
};

const normalizeItem = (item) => {
  if (!item || typeof item !== 'object') {
    return {
      id: null,
      name: 'Item sem nome',
      sku: '',
      category: normalizeCategory(null),
      warehouse: normalizeWarehouse(null),
      warehouses: [],
      unit: 'un',
      stock: 0,
      reserved: 0,
      minimumStock: 0,
      maximumStock: 0,
      safetyStock: 0,
      costPrice: 0,
      salePrice: 0,
      active: true,
      updatedAt: null,
      lastMovementAt: null,
      expirationDate: null,
    };
  }

  const category = normalizeCategory(item.category ?? {
    id: item.categoryId,
    name: item.categoryName,
    description: item.categoryDescription,
  });

  const warehouse = normalizeWarehouse(item.warehouse ?? {
    id: item.warehouseId,
    name: item.warehouseName,
    code: item.warehouseCode,
  });

  const warehouseEntries = Array.isArray(item.warehouseStocks)
    ? item.warehouseStocks.map((stock) => {
        const normalized = normalizeWarehouse(stock.warehouse ?? {
          id: stock.warehouseId ?? stock.id ?? null,
          name: stock.warehouseName,
          code: stock.warehouseCode,
        });
        return {
          ...normalized,
          quantity: Number(stock.quantity ?? stock.amount ?? stock.stock ?? 0),
        };
      })
    : [];

  const primaryWarehouse =
    warehouseEntries.find((entry) => entry.id === warehouse.id) ||
    warehouseEntries.find((entry) => entry.quantity > 0) ||
    warehouseEntries[0] ||
    warehouse;

  const stockFromEntries = warehouseEntries.reduce(
    (total, entry) => total + Number(entry.quantity || 0),
    0,
  );
  const stockFallback = Number(
    item.stock ?? item.availableQuantity ?? item.currentQuantity ?? 0,
  );
  const stock =
    warehouseEntries.length > 0 && Number.isFinite(stockFromEntries)
      ? stockFromEntries
      : stockFallback;
  const reserved = Number(item.reserved ?? item.reservedQuantity ?? 0);
  const minimumStock = Number(
    item.minimumStock ?? item.reorderPoint ?? item.minimumQuantity ?? 0,
  );
  const maximumStock = Number(item.maximumStock ?? item.maximumQuantity ?? 0);
  const safetyStock = Number(item.safetyStock ?? item.bufferQuantity ?? 0);

  return {
    id: item.id ?? item.itemId ?? null,
    name: item.name ?? item.title ?? 'Item sem nome',
    sku: item.sku ?? item.code ?? item.reference ?? '',
    category,
    warehouse: primaryWarehouse,
    warehouses: warehouseEntries,
    unit: item.unit ?? item.measureUnit ?? 'un',
    stock,
    reserved,
    minimumStock,
    maximumStock,
    safetyStock,
    costPrice: Number(item.costPrice ?? item.averageCost ?? 0),
    salePrice: Number(item.salePrice ?? item.price ?? 0),
    active: item.active ?? item.enabled ?? true,
    lastMovementAt: item.lastMovementAt ?? item.lastMovementDate ?? null,
    updatedAt: item.updatedAt ?? item.updatedAt ?? null,
    trackBatch: Boolean(item.trackBatch ?? item.batchControl ?? false),
    trackExpiration: Boolean(
      item.trackExpiration ?? item.expirationControl ?? false,
    ),
    description: item.description ?? item.notes ?? '',
    expirationDate: item.expirationDate ?? item.validUntil ?? null,
  };
};

const normalizeMovement = (movement) => {
  if (!movement || typeof movement !== 'object') {
    return {
      id: null,
      type: 'INBOUND',
      quantity: 0,
      unitCost: 0,
      occurredAt: null,
      referenceCode: '',
      notes: '',
      item: normalizeItem(null),
      originWarehouse: normalizeWarehouse(null),
      targetWarehouse: normalizeWarehouse(null),
    };
  }

  return {
    id: movement.id ?? movement.movementId ?? null,
    type: movement.type ?? movement.movementType ?? 'INBOUND',
    quantity: Number(movement.quantity ?? movement.amount ?? 0),
    unitCost: Number(movement.unitCost ?? movement.cost ?? 0),
    occurredAt: movement.occurredAt ?? movement.date ?? null,
    referenceCode: movement.referenceCode ?? movement.reference ?? '',
    notes: movement.notes ?? movement.description ?? '',
    item: normalizeItem(movement.item ?? movement.inventoryItem ?? null),
    originWarehouse: normalizeWarehouse(
      movement.originWarehouse ?? movement.fromWarehouse ?? null,
    ),
    targetWarehouse: normalizeWarehouse(
      movement.targetWarehouse ?? movement.toWarehouse ?? null,
    ),
  };
};

const buildItemPayload = (form) => ({
  name: form.name.trim(),
  sku: form.sku.trim() || null,
  categoryId:
    form.categoryId && Number.isFinite(Number(form.categoryId))
      ? Number(form.categoryId)
      : null,
  warehouseId:
    form.warehouseId && Number.isFinite(Number(form.warehouseId))
      ? Number(form.warehouseId)
      : null,
  unit: form.unit.trim() || 'un',
  costPrice: form.costPrice ? Number(form.costPrice) : null,
  salePrice: form.salePrice ? Number(form.salePrice) : null,
  minimumStock: form.minimumStock ? Number(form.minimumStock) : null,
  maximumStock: form.maximumStock ? Number(form.maximumStock) : null,
  safetyStock: form.safetyStock ? Number(form.safetyStock) : null,
  description: form.description?.trim() || null,
  trackBatch: Boolean(form.trackBatch),
  trackExpiration: Boolean(form.trackExpiration),
  expirationDate: form.expirationDate
    ? new Date(`${form.expirationDate}T00:00:00`).toISOString()
    : null,
  active: Boolean(form.active),
});

const buildWarehousePayload = (form) => ({
  name: form.name.trim(),
  code: form.code.trim() || null,
  manager: form.manager.trim() || null,
  phone: form.phone.trim() || null,
  email: form.email.trim() || null,
  address: form.address.trim() || null,
  notes: form.notes.trim() || null,
});

const buildCategoryPayload = (form) => ({
  name: form.name.trim(),
  description: form.description.trim() || null,
});

const buildMovementPayload = (form) => ({
  itemId:
    form.itemId && Number.isFinite(Number(form.itemId))
      ? Number(form.itemId)
      : null,
  type: form.type,
  quantity: Number(form.quantity || 0),
  originWarehouseId:
    form.type === 'INBOUND'
      ? null
      : form.originWarehouseId && Number.isFinite(Number(form.originWarehouseId))
      ? Number(form.originWarehouseId)
      : null,
  targetWarehouseId:
    form.type === 'OUTBOUND'
      ? null
      : form.targetWarehouseId && Number.isFinite(Number(form.targetWarehouseId))
      ? Number(form.targetWarehouseId)
      : null,
  unitCost: form.unitCost ? Number(form.unitCost) : null,
  occurredAt: form.occurredAt ? new Date(form.occurredAt).toISOString() : null,
  referenceCode: form.referenceCode?.trim() || null,
  notes: form.notes?.trim() || null,
});

export default function Inventory() {
  const { user } = useAuth();
  const canManage = hasPermission(user, INVENTORY_MANAGE_PERMISSION);
  const canView =
    canManage || hasPermission(user, INVENTORY_VIEW_PERMISSION);

  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [movements, setMovements] = useState([]);
  const [error, setError] = useState('');
  const [feedback, setFeedback] = useState('');
  const [feedbackType, setFeedbackType] = useState('success');
  const [reportFormat, setReportFormat] = useState('pdf');
  const [importPreviewRows, setImportPreviewRows] = useState([]);
  const [importingStock, setImportingStock] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('items');
  const [aiSettings, setAiSettings] = useState({ loading: true, configured: false });
  const [aiReport, setAiReport] = useState(null);
  const [aiReportLoading, setAiReportLoading] = useState(false);
  const [aiFilters, setAiFilters] = useState({ warehouseId: 'all', categoryId: 'all', focus: '' });
  const importPreviewRef = useRef(null);

  const [filters, setFilters] = useState({
    search: '',
    warehouseId: 'all',
    categoryId: 'all',
    status: 'all',
  });

  const [movementFilters, setMovementFilters] = useState({
    type: 'all',
    warehouseId: 'all',
    period: '30',
  });

  const [itemModalOpen, setItemModalOpen] = useState(false);
  const [itemForm, setItemForm] = useState(emptyItemForm);
  const [savingItem, setSavingItem] = useState(false);

  const [movementModalOpen, setMovementModalOpen] = useState(false);
  const [movementForm, setMovementForm] = useState(emptyMovementForm);
  const [savingMovement, setSavingMovement] = useState(false);

  const [warehouseModalOpen, setWarehouseModalOpen] = useState(false);
  const [warehouseForm, setWarehouseForm] = useState(emptyWarehouseForm);
  const [savingWarehouse, setSavingWarehouse] = useState(false);
  const [bulkUpdateMessage, setBulkUpdateMessage] = useState('');
  const [itemsPage, setItemsPage] = useState(1);
  const [itemsPageSize, setItemsPageSize] = useState(DEFAULT_TABLE_PAGE_SIZE);
  const [movementsPage, setMovementsPage] = useState(1);
  const [movementsPageSize, setMovementsPageSize] = useState(DEFAULT_TABLE_PAGE_SIZE);

  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [categoryForm, setCategoryForm] = useState(emptyCategoryForm);
  const [savingCategory, setSavingCategory] = useState(false);

  const showFeedback = useCallback((message, type = 'success') => {
    setFeedback(message);
    setFeedbackType(type);
    if (message) {
      setTimeout(() => {
        setFeedback('');
      }, 6000);
    }
  }, []);

  const handleError = useCallback((message) => {
    setError(message);
    if (message) {
      setTimeout(() => setError(''), 8000);
    }
  }, []);

  const loadItems = useCallback(async () => {
    try {
      const response = await InventoryService.listItems({});
      const data = Array.isArray(response.data?.data)
        ? response.data.data
        : Array.isArray(response.data)
        ? response.data
        : [];
      setItems(data.map(normalizeItem));
    } catch (err) {
      handleError(resolveErrorMessage(err));
      setItems([]);
    }
  }, [handleError]);

  const loadWarehouses = useCallback(async () => {
    try {
      const response = await InventoryService.listWarehouses({});
      const data = Array.isArray(response.data?.data)
        ? response.data.data
        : Array.isArray(response.data)
        ? response.data
        : [];
      setWarehouses(data.map(normalizeWarehouse));
    } catch (err) {
      handleError(resolveErrorMessage(err));
      setWarehouses([]);
    }
  }, [handleError]);

  const loadCategories = useCallback(async () => {
    try {
      const response = await InventoryService.listCategories({});
      const data = Array.isArray(response.data?.data)
        ? response.data.data
        : Array.isArray(response.data)
        ? response.data
        : [];
      setCategories(data.map(normalizeCategory));
    } catch (err) {
      handleError(resolveErrorMessage(err));
      setCategories([]);
    }
  }, [handleError]);

  const loadMovements = useCallback(async () => {
    try {
      const response = await InventoryService.listMovements({ limit: 100 });
      const data = Array.isArray(response.data?.data)
        ? response.data.data
        : Array.isArray(response.data)
        ? response.data
        : [];
      setMovements(data.map(normalizeMovement));
    } catch (err) {
      handleError(resolveErrorMessage(err));
      setMovements([]);
    }
  }, [handleError]);

  const loadAll = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    await Promise.allSettled([
      loadItems(),
      loadWarehouses(),
      loadCategories(),
      loadMovements(),
    ]);
    setLoading(false);
  }, [canView, loadItems, loadWarehouses, loadCategories, loadMovements]);

  useEffect(() => {
    if (!canView) return;
    loadAll();
  }, [canView, loadAll]);

  useEffect(() => {
    window.localStorage.setItem(INVENTORY_TAB_STORAGE_KEY, activeTab);
  }, [activeTab]);

  useEffect(() => {
    const key = getWarehousePreferenceKey(user);
    const preferredWarehouseId = window.localStorage.getItem(key);
    if (preferredWarehouseId) {
      setFilters((prev) => ({ ...prev, warehouseId: preferredWarehouseId }));
      setMovementFilters((prev) => ({ ...prev, warehouseId: preferredWarehouseId }));
      setAiFilters((prev) => ({ ...prev, warehouseId: preferredWarehouseId }));
    }
  }, [user]);

  useEffect(() => {
    let isMounted = true;
    ProjectBankService.getAIIntegrationSettings()
      .then((settings) => {
        if (isMounted) setAiSettings({ loading: false, configured: Boolean(settings?.nashOpenAiApiKeyConfigured || settings?.openAiApiKeyConfigured) });
      })
      .catch(() => {
        if (isMounted) setAiSettings({ loading: false, configured: false });
      });
    return () => { isMounted = false; };
  }, []);

  const lowStockItems = useMemo(
    () =>
      items.filter((item) =>
        Number.isFinite(item.minimumStock)
          ? item.stock <= item.minimumStock
          : false,
      ),
    [items],
  );

  const totalInventoryValue = useMemo(
    () =>
      items.reduce((total, item) => {
        const stock = Number(item.stock || 0);
        const cost = Number(item.costPrice || 0);
        return total + stock * cost;
      }, 0),
    [items],
  );

  const selectedWarehouse = useMemo(() => warehouses.find((warehouse) => String(warehouse.id ?? '') === String(filters.warehouseId)), [warehouses, filters.warehouseId]);

  const warehouseDashboard = useMemo(() => {
    return warehouses.map((warehouse) => {
      const stockEntries = items.flatMap((item) => (item.warehouses || [])
        .filter((entry) => String(entry.id ?? '') === String(warehouse.id ?? ''))
        .map((entry) => ({ item, quantity: Number(entry.quantity || 0), value: Number(entry.quantity || 0) * Number(item.costPrice || 0) })));
      return { ...warehouse, itemCount: stockEntries.length, quantity: stockEntries.reduce((sum, entry) => sum + entry.quantity, 0), value: stockEntries.reduce((sum, entry) => sum + entry.value, 0) };
    }).sort((a, b) => b.value - a.value);
  }, [items, warehouses]);

  const recentOutboundValue = useMemo(() => {
    const now = Date.now();
    const limitDate =
      movementFilters.period === '0'
        ? null
        : now - Number(movementFilters.period || 30) * 24 * 60 * 60 * 1000;
    return movements
      .filter((movement) => {
        if (movement.type !== 'OUTBOUND') return false;
        if (!limitDate) return true;
        const date = movement.occurredAt
          ? new Date(movement.occurredAt).getTime()
          : 0;
        return Number.isFinite(date) && date >= limitDate;
      })
      .reduce((total, movement) => {
        const quantity = Number(movement.quantity || 0);
        const unitCost = Number(movement.unitCost || 0);
        return total + quantity * unitCost;
      }, 0);
  }, [movements, movementFilters.period]);

  const filteredItems = useMemo(() => {
    const term = filters.search.trim().toLowerCase();
    return items.filter((item) => {
      const matchesTerm = term
        ? [
            item.name,
            item.sku,
            item.category?.name,
            item.warehouse?.name,
            ...(Array.isArray(item.warehouses)
              ? item.warehouses.map((warehouse) => warehouse.name)
              : []),
          ]
            .filter(Boolean)
            .some((value) => value.toLowerCase().includes(term))
        : true;

      const matchesWarehouse =
        filters.warehouseId === 'all' ||
        [
          item.warehouse?.id,
          ...(Array.isArray(item.warehouses)
            ? item.warehouses.map((warehouse) => warehouse.id)
            : []),
        ]
          .filter((id) => id !== undefined && id !== null)
          .some((id) => String(id ?? '') === String(filters.warehouseId));

      const matchesCategory =
        filters.categoryId === 'all' ||
        String(item.category?.id ?? '') === String(filters.categoryId);

      const status = (() => {
        if (!item.active) return 'inactive';
        if (item.minimumStock && item.stock <= item.minimumStock) return 'low';
        if (item.maximumStock && item.stock > item.maximumStock) return 'excess';
        return 'ok';
      })();

      const matchesStatus = filters.status === 'all' || status === filters.status;

      return matchesTerm && matchesWarehouse && matchesCategory && matchesStatus;
    });
  }, [items, filters]);


  const categoryDashboard = useMemo(() => {
    const map = new Map();
    filteredItems.forEach((item) => {
      const key = item.category?.name || 'Sem categoria';
      const current = map.get(key) || { name: key, value: 0, quantity: 0, itemCount: 0 };
      current.value += Number(item.stock || 0) * Number(item.costPrice || 0);
      current.quantity += Number(item.stock || 0);
      current.itemCount += 1;
      map.set(key, current);
    });
    return Array.from(map.values()).sort((a, b) => b.value - a.value);
  }, [filteredItems]);

  const saveWarehousePreference = useCallback(() => {
    window.localStorage.setItem(getWarehousePreferenceKey(user), filters.warehouseId);
    showFeedback(filters.warehouseId === 'all' ? 'Preferência salva: abrir estoque com todos os depósitos.' : `Preferência salva: abrir direto no depósito ${selectedWarehouse?.name || 'selecionado'}.`);
  }, [filters.warehouseId, selectedWarehouse?.name, showFeedback, user]);

  const generateAiReport = useCallback(async () => {
    if (!aiSettings.configured) return;
    setAiReportLoading(true);
    try {
      const response = await InventoryService.generateAIReport({ ...aiFilters, format: 'pdf' });
      const report = response.data?.data || response.data;
      setAiReport(report);
      if (report?.pdfBase64) {
        const binary = window.atob(report.pdfBase64);
        const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
        const blob = new Blob([bytes], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank', 'noopener,noreferrer');
        setTimeout(() => window.URL.revokeObjectURL(url), 30000);
      }
      setActiveTab('ai');
    } catch (err) {
      handleError(resolveErrorMessage(err));
    } finally {
      setAiReportLoading(false);
    }
  }, [aiFilters, aiSettings.configured, handleError]);

  const selectedMovementItem = useMemo(() => {
    if (!movementForm.itemId) return null;
    return items.find(
      (candidate) => String(candidate.id ?? '') === String(movementForm.itemId),
    );
  }, [items, movementForm.itemId]);

  const selectedMovementType = useMemo(
    () =>
      MOVEMENT_TYPES.find((movement) => movement.value === movementForm.type) ||
      MOVEMENT_TYPES[0],
    [movementForm.type],
  );
  const SelectedMovementIcon = selectedMovementType?.icon ?? FaExchangeAlt;
  const movementTone =
    movementForm.type === 'INBOUND'
      ? 'inbound'
      : movementForm.type === 'OUTBOUND'
      ? 'outbound'
      : 'transfer';
  const selectedMovementStockStatus = useMemo(() => {
    if (!selectedMovementItem) {
      return { label: 'Selecione um item', tone: 'neutral' };
    }
    const stock = Number(selectedMovementItem.stock || 0);
    const minimum = Number(selectedMovementItem.minimumStock || 0);
    if (stock <= 0) return { label: 'Sem estoque', tone: 'danger' };
    if (minimum > 0 && stock <= minimum) {
      return { label: 'Estoque baixo', tone: 'warning' };
    }
    return { label: 'Estoque normal', tone: 'success' };
  }, [selectedMovementItem]);

  const filteredMovements = useMemo(() => {
    const now = Date.now();
    const periodDays = Number(movementFilters.period);
    const limitDate =
      movementFilters.period === '0'
        ? null
        : Number.isFinite(periodDays)
        ? now - periodDays * 24 * 60 * 60 * 1000
        : null;

    return movements.filter((movement) => {
      const matchesType =
        movementFilters.type === 'all' || movement.type === movementFilters.type;

      const matchesWarehouse = (() => {
        if (movementFilters.warehouseId === 'all') return true;
        const warehouseId = String(movementFilters.warehouseId);
        return (
          String(movement.originWarehouse?.id ?? '') === warehouseId ||
          String(movement.targetWarehouse?.id ?? '') === warehouseId
        );
      })();

      const matchesPeriod = (() => {
        if (!limitDate) return true;
        const movementDate = movement.occurredAt
          ? new Date(movement.occurredAt).getTime()
          : null;
        return Number.isFinite(movementDate) ? movementDate >= limitDate : false;
      })();

      return matchesType && matchesWarehouse && matchesPeriod;
    });
  }, [movements, movementFilters]);

  useEffect(() => {
    setItemsPage(1);
  }, [filters, itemsPageSize]);

  useEffect(() => {
    setMovementsPage(1);
  }, [movementFilters, movementsPageSize]);

  const itemsPagination = paginateTableRows(filteredItems, itemsPage, itemsPageSize);
  const movementsPagination = paginateTableRows(
    filteredMovements,
    movementsPage,
    movementsPageSize,
  );
  const paginatedItems = itemsPagination.rows;
  const paginatedMovements = movementsPagination.rows;

  useEffect(() => {
    if (itemsPage !== itemsPagination.page) {
      setItemsPage(itemsPagination.page);
    }
  }, [itemsPage, itemsPagination.page]);

  useEffect(() => {
    if (movementsPage !== movementsPagination.page) {
      setMovementsPage(movementsPagination.page);
    }
  }, [movementsPage, movementsPagination.page]);

  const toItemForm = useCallback((item) => ({
    id: item?.id ?? null,
    name: item?.name ?? '',
    sku: item?.sku ?? '',
    categoryId: item?.category?.id ?? '',
    warehouseId: item?.warehouse?.id ?? '',
    unit: item?.unit ?? 'un',
    costPrice: item?.costPrice ? String(item.costPrice) : '',
    salePrice: item?.salePrice ? String(item.salePrice) : '',
    minimumStock: item?.minimumStock ? String(item.minimumStock) : '',
    maximumStock: item?.maximumStock ? String(item.maximumStock) : '',
    safetyStock: item?.safetyStock ? String(item.safetyStock) : '',
    description: item?.description ?? '',
    trackBatch: Boolean(item?.trackBatch),
    trackExpiration: Boolean(item?.trackExpiration),
    expirationDate: toInputDateValue(item?.expirationDate),
    active: Boolean(item?.active ?? true),
  }), []);

  const toWarehouseForm = useCallback((warehouse) => ({
    id: warehouse?.id ?? null,
    name: warehouse?.name ?? '',
    code: warehouse?.code ?? '',
    manager: warehouse?.manager ?? '',
    phone: warehouse?.phone ?? '',
    email: warehouse?.email ?? '',
    address: warehouse?.address ?? '',
    notes: warehouse?.notes ?? '',
  }), []);

  const toCategoryForm = useCallback((category) => ({
    id: category?.id ?? null,
    name: category?.name ?? '',
    description: category?.description ?? '',
  }), []);

  const openItemModal = useCallback(
    (item = null) => {
      setItemForm(toItemForm(item));
      setItemModalOpen(true);
    },
    [toItemForm],
  );

  const openWarehouseModal = useCallback(
    (warehouse = null) => {
      setWarehouseForm(toWarehouseForm(warehouse));
      setWarehouseModalOpen(true);
    },
    [toWarehouseForm],
  );

  const openCategoryModal = useCallback(
    (category = null) => {
      setCategoryForm(toCategoryForm(category));
      setCategoryModalOpen(true);
    },
    [toCategoryForm],
  );

  const openMovementModal = useCallback(
    (item = null) => {
      const preferredWarehouse = item?.warehouses?.find(
        (warehouse) => Number(warehouse.quantity || 0) > 0,
      );
      const defaultWarehouseId =
        preferredWarehouse?.id ?? item?.warehouse?.id ?? '';
      const defaultWarehouseValue =
        defaultWarehouseId !== '' ? String(defaultWarehouseId) : '';
      setMovementForm({
        ...emptyMovementForm,
        itemId: item?.id ? String(item.id) : '',
        originWarehouseId: defaultWarehouseValue,
        targetWarehouseId: defaultWarehouseValue,
      });
      setMovementModalOpen(true);
    },
    [],
  );

  const handleDeleteItem = useCallback(
    async (item) => {
      if (!canManage || !item?.id) return;
      const confirmed = window.confirm(
        `Excluir o item "${item.name}"? Essa ação não pode ser desfeita.`,
      );
      if (!confirmed) return;
      try {
        await InventoryService.deleteItem(item.id);
        showFeedback('Item excluído com sucesso.');
        loadItems();
      } catch (err) {
        handleError(resolveErrorMessage(err));
      }
    },
    [canManage, handleError, loadItems, showFeedback],
  );

  const handleDeleteWarehouse = useCallback(
    async (warehouse) => {
      if (!canManage || !warehouse?.id) return;
      const confirmed = window.confirm(
        `Excluir o depósito "${warehouse.name}"?`,
      );
      if (!confirmed) return;
      try {
        await InventoryService.deleteWarehouse(warehouse.id);
        showFeedback('Depósito excluído com sucesso.');
        loadWarehouses();
        loadItems();
      } catch (err) {
        handleError(resolveErrorMessage(err));
      }
    },
    [canManage, handleError, loadItems, loadWarehouses, showFeedback],
  );

  const handleDeleteCategory = useCallback(
    async (category) => {
      if (!canManage || !category?.id) return;
      const confirmed = window.confirm(
        `Excluir a categoria "${category.name}"?`,
      );
      if (!confirmed) return;
      try {
        await InventoryService.deleteCategory(category.id);
        showFeedback('Categoria excluída com sucesso.');
        loadCategories();
        loadItems();
      } catch (err) {
        handleError(resolveErrorMessage(err));
      }
    },
    [canManage, handleError, loadCategories, loadItems, showFeedback],
  );

  const handleSaveItem = useCallback(
    async (event) => {
      event.preventDefault();
      if (!canManage) return;
      setSavingItem(true);
      try {
        const payload = buildItemPayload(itemForm);
        if (itemForm.id) {
          await InventoryService.updateItem(itemForm.id, payload);
          showFeedback('Item atualizado com sucesso.');
        } else {
          await InventoryService.createItem(payload);
          showFeedback('Item cadastrado com sucesso.');
        }
        setItemModalOpen(false);
        setItemForm({ ...emptyItemForm });
        loadItems();
      } catch (err) {
        handleError(resolveErrorMessage(err));
      } finally {
        setSavingItem(false);
      }
    },
    [canManage, handleError, itemForm, loadItems, showFeedback],
  );

  const handleSaveWarehouse = useCallback(
    async (event) => {
      event.preventDefault();
      if (!canManage) return;
      setSavingWarehouse(true);
      try {
        const payload = buildWarehousePayload(warehouseForm);
        if (warehouseForm.id) {
          setBulkUpdateMessage('Atualizando todos os itens vinculados a este depósito...');
          const response = await InventoryService.updateWarehouse(warehouseForm.id, payload);
          const updatedCount = response.data?.linkedItemsUpdated ?? 0;
          showFeedback(updatedCount ? `Depósito atualizado e ${updatedCount} item(ns) vinculado(s) sincronizado(s).` : 'Depósito atualizado com sucesso.');
        } else {
          await InventoryService.createWarehouse(payload);
          showFeedback('Depósito cadastrado com sucesso.');
        }
        setWarehouseModalOpen(false);
        setWarehouseForm({ ...emptyWarehouseForm });
        loadWarehouses();
        loadItems();
      } catch (err) {
        handleError(resolveErrorMessage(err));
      } finally {
        setSavingWarehouse(false);
        setBulkUpdateMessage('');
      }
    },
    [canManage, handleError, loadItems, loadWarehouses, showFeedback, warehouseForm],
  );

  const handleSaveCategory = useCallback(
    async (event) => {
      event.preventDefault();
      if (!canManage) return;
      setSavingCategory(true);
      try {
        const payload = buildCategoryPayload(categoryForm);
        if (categoryForm.id) {
          setBulkUpdateMessage('Atualizando todos os itens vinculados a esta categoria...');
          const response = await InventoryService.updateCategory(categoryForm.id, payload);
          const updatedCount = response.data?.linkedItemsUpdated ?? 0;
          showFeedback(updatedCount ? `Categoria atualizada e ${updatedCount} item(ns) vinculado(s) sincronizado(s).` : 'Categoria atualizada com sucesso.');
        } else {
          await InventoryService.createCategory(payload);
          showFeedback('Categoria cadastrada com sucesso.');
        }
        setCategoryModalOpen(false);
        setCategoryForm({ ...emptyCategoryForm });
        loadCategories();
        loadItems();
      } catch (err) {
        handleError(resolveErrorMessage(err));
      } finally {
        setSavingCategory(false);
        setBulkUpdateMessage('');
      }
    },
    [canManage, categoryForm, handleError, loadCategories, loadItems, showFeedback],
  );

  const handleSaveMovement = useCallback(
    async (event) => {
      event.preventDefault();
      if (!canManage) return;
      setSavingMovement(true);
      try {
        const payload = buildMovementPayload(movementForm);
        await InventoryService.registerMovement(payload);
        showFeedback('Movimentação registrada com sucesso.');
        setMovementModalOpen(false);
        setMovementForm({ ...emptyMovementForm });
        loadItems();
        loadMovements();
      } catch (err) {
        handleError(resolveErrorMessage(err));
      } finally {
        setSavingMovement(false);
      }
    },
    [canManage, handleError, loadItems, loadMovements, movementForm, showFeedback],
  );

  const handleDownloadReport = useCallback(async () => {
    try {
      const response = await InventoryService.downloadReport({
        warehouseId:
          filters.warehouseId !== 'all' ? filters.warehouseId : undefined,
        categoryId:
          filters.categoryId !== 'all' ? filters.categoryId : undefined,
        format: reportFormat,
      });
      const disposition = response.headers?.['content-disposition'] || '';
      const match = disposition.match(
        /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i,
      );
      const resolvedName = match?.[1] || match?.[2];
      const fallbackExtension =
        reportFormat === 'pdf' ? 'pdf' : reportFormat === 'xlsx' ? 'xlsx' : 'xls';
      const fileName = resolvedName
        ? decodeURIComponent(resolvedName.replace(/"/g, ''))
        : `relatorio-estoque.${fallbackExtension}`;

      const contentType =
        response.headers?.['content-type'] ||
        (reportFormat === 'pdf'
          ? 'application/pdf'
          : reportFormat === 'xlsx'
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          : 'application/vnd.ms-excel');

      const blob = new Blob([response.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      handleError(resolveErrorMessage(err));
    }
  }, [filters.categoryId, filters.warehouseId, handleError, reportFormat]);

  const handleDownloadImportTemplate = useCallback(() => {
    try {
      const categoryOptions = categories.map((category) => category.name).filter(Boolean);
      const warehouseOptions = warehouses
        .map((warehouse) => [warehouse.name, warehouse.code].filter(Boolean).join(' / '))
        .filter(Boolean);
      const booleanOptions = 'Sim | Não';

      const templateRows = [
        IMPORT_COLUMNS.reduce(
          (acc, column) => ({ ...acc, [getImportColumnLabel(column)]: '' }),
          {},
        ),
      ];
      const worksheet = XLSXUtils.json_to_sheet(templateRows, {
        header: IMPORT_COLUMNS.map((column) => getImportColumnLabel(column)),
        skipHeader: false,
      });
      worksheet['!cols'] = IMPORT_COLUMNS.map((column) => ({
        wch: Math.max(column.label.length + 3, 20),
      }));
      applyNashHeaderStyle(
        worksheet,
        IMPORT_COLUMNS.map((column) => getImportColumnLabel(column)),
      );

      const instructionsRows = IMPORT_COLUMNS.map((column) => ({
        Coluna: getImportColumnLabel(column),
        Obrigatorio: column.required ? 'Sim' : 'Não',
        Orientacao: column.instructions,
      }));
      const instructionsSheet = XLSXUtils.json_to_sheet(instructionsRows);
      instructionsSheet['!cols'] = [{ wch: 30 }, { wch: 12 }, { wch: 70 }];
      applyNashHeaderStyle(instructionsSheet, ['Coluna', 'Obrigatorio', 'Orientacao']);

      const optionsSheet = XLSXUtils.json_to_sheet([
        { Tipo: 'Categorias cadastradas', Opcoes: categoryOptions.join(' | ') || 'Sem categorias cadastradas' },
        { Tipo: 'Depósitos cadastrados', Opcoes: warehouseOptions.join(' | ') || 'Sem depósitos cadastrados' },
        { Tipo: 'Campos Sim/Não', Opcoes: booleanOptions },
      ]);
      optionsSheet['!cols'] = [{ wch: 28 }, { wch: 120 }];
      applyNashHeaderStyle(optionsSheet, ['Tipo', 'Opcoes']);

      const workbook = XLSXUtils.book_new();
      XLSXUtils.book_append_sheet(workbook, worksheet, 'Estoque');
      XLSXUtils.book_append_sheet(workbook, instructionsSheet, 'Instrucoes');
      XLSXUtils.book_append_sheet(workbook, optionsSheet, 'Opcoes');
      writeXLSXFile(workbook, 'template-importacao-estoque.xlsx', { bookType: 'xlsx' });
      showFeedback('Template de importação gerado com sucesso.');
    } catch (err) {
      handleError('Não foi possível gerar o template de importação.');
    }
  }, [categories, handleError, showFeedback, warehouses]);

  const scrollToImportPreview = useCallback(() => {
    window.setTimeout(() => {
      importPreviewRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  }, []);

  const handleImportFileChange = useCallback(
    async (event) => {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (!file) return;

      try {
        const buffer = await file.arrayBuffer();
        const workbook = readXLSX(buffer, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        if (!worksheet) {
          throw new Error('Planilha inválida. Não foi encontrada nenhuma aba de dados.');
        }

        const rows = XLSXUtils.sheet_to_json(worksheet, { defval: '' });
        if (!rows.length) {
          setImportPreviewRows([]);
          handleError('A planilha está vazia.');
          return;
        }

        const categoryByName = new Map(categories.map((category) => [normalizeText(category.name), category]));
        const warehouseByNameOrCode = new Map();
        warehouses.forEach((warehouse) => {
          if (warehouse.name) warehouseByNameOrCode.set(normalizeText(warehouse.name), warehouse);
          if (warehouse.code) warehouseByNameOrCode.set(normalizeText(warehouse.code), warehouse);
        });

        const preview = rows.map((row, index) => {
          const normalizedRow = {};
          Object.entries(row).forEach(([header, value]) => {
            normalizedRow[normalizeHeader(header)] = value;
          });

          const payload = {};
          IMPORT_COLUMNS.forEach((column) => {
            const canonicalHeader = normalizeHeader(column.label);
            const starredHeader = normalizeHeader(getImportColumnLabel(column));
            payload[column.key] = normalizedRow[starredHeader] ?? normalizedRow[canonicalHeader];
          });

          const textPayload = Object.fromEntries(
            Object.entries(payload).map(([key, value]) => [key, resolveTextValue(value)]),
          );
          const errors = [];
          IMPORT_REQUIRED_FIELDS.forEach((field) => {
            if (!textPayload[field]) {
              const column = IMPORT_COLUMNS.find((item) => item.key === field);
              errors.push(`"${column?.label || field}" é obrigatório.`);
            }
          });

          const category = textPayload.category
            ? categoryByName.get(normalizeText(textPayload.category))
            : null;
          const warehouse = warehouseByNameOrCode.get(normalizeText(textPayload.warehouse));
          const quantity = parseImportNumber(payload.quantity);
          const costPrice = parseImportNumber(payload.costPrice);
          const salePrice = parseImportNumber(payload.salePrice);
          const minimumStock = parseImportNumber(payload.minimumStock);
          const maximumStock = parseImportNumber(payload.maximumStock);
          const safetyStock = parseImportNumber(payload.safetyStock);

          if (textPayload.category && !category) errors.push(`Categoria "${textPayload.category}" não cadastrada.`);
          if (textPayload.warehouse && !warehouse) errors.push(`Depósito "${textPayload.warehouse}" não cadastrado.`);
          if (quantity === null || quantity < 0) errors.push('"Quantidade inicial" deve ser um número maior ou igual a zero.');
          if (costPrice !== null && costPrice < 0) errors.push('"Custo de compra" deve ser maior ou igual a zero.');
          if (salePrice !== null && salePrice < 0) errors.push('"Preço de venda" deve ser maior ou igual a zero.');
          if (minimumStock !== null && minimumStock < 0) errors.push('"Estoque mínimo" deve ser maior ou igual a zero.');
          if (maximumStock !== null && maximumStock < 0) errors.push('"Estoque máximo" deve ser maior ou igual a zero.');
          if (safetyStock !== null && safetyStock < 0) errors.push('"Estoque de segurança" deve ser maior ou igual a zero.');

          return {
            rowNumber: index + 2,
            payload: {
              ...textPayload,
              categoryId: category?.id || '',
              warehouseId: warehouse?.id || '',
              warehouseName: warehouse?.name || textPayload.warehouse,
              unit: textPayload.unit || 'un',
              quantity,
              costPrice,
              salePrice,
              minimumStock,
              maximumStock,
              safetyStock,
              expirationDate: parseImportDate(payload.expirationDate),
              trackBatch: parseImportBoolean(payload.trackBatch, false),
              trackExpiration: parseImportBoolean(payload.trackExpiration, false),
              active: parseImportBoolean(payload.active, true),
            },
            errors,
          };
        });

        setImportPreviewRows(preview);
        const invalidCount = preview.filter((item) => item.errors.length > 0).length;
        if (invalidCount > 0) {
          showFeedback(`Prévia carregada com ${preview.length} linha(s). ${invalidCount} linha(s) com pendências.`, 'warning');
        } else {
          showFeedback(`Prévia carregada com ${preview.length} linha(s) pronta(s) para confirmação.`);
        }
        setImportModalOpen(false);
        scrollToImportPreview();
      } catch (err) {
        handleError(err?.message || 'Não foi possível ler a planilha.');
        setImportPreviewRows([]);
      }
    },
    [categories, handleError, scrollToImportPreview, showFeedback, warehouses],
  );

  const handleConfirmImport = useCallback(async () => {
    if (!importPreviewRows.length) {
      handleError('Carregue uma planilha antes de confirmar a importação.');
      return;
    }

    const invalidRows = importPreviewRows.filter((row) => row.errors.length > 0);
    if (invalidRows.length > 0) {
      const missingRequired = new Set();
      invalidRows.forEach((row) => {
        row.errors.forEach((message) => {
          if (message.includes('obrigatório')) {
            missingRequired.add(message.replace(' é obrigatório.', ''));
          }
        });
      });
      handleError(
        `Existem ${invalidRows.length} linha(s) inválida(s). Campos obrigatórios faltantes: ${
          missingRequired.size ? Array.from(missingRequired).join(', ') : 'revise a planilha'
        }.`,
      );
      return;
    }

    setImportingStock(true);
    try {
      for (const row of importPreviewRows) {
        const { data } = await InventoryService.createItem({
          name: row.payload.name,
          sku: row.payload.sku || null,
          categoryId: row.payload.categoryId || null,
          warehouseId: row.payload.warehouseId || null,
          unit: row.payload.unit || 'un',
          costPrice: row.payload.costPrice ?? null,
          salePrice: row.payload.salePrice ?? null,
          minimumStock: row.payload.minimumStock ?? null,
          maximumStock: row.payload.maximumStock ?? null,
          safetyStock: row.payload.safetyStock ?? null,
          description: row.payload.description || null,
          trackBatch: row.payload.trackBatch,
          trackExpiration: row.payload.trackExpiration,
          expirationDate: row.payload.expirationDate || null,
          active: row.payload.active,
        });
        const createdItem = data?.data || data;
        const itemId = createdItem?.id;
        if (itemId && row.payload.quantity > 0) {
          await InventoryService.registerMovement({
            itemId,
            type: 'INBOUND',
            quantity: row.payload.quantity,
            targetWarehouseId: row.payload.warehouseId || null,
            unitCost: row.payload.costPrice ?? null,
            occurredAt: new Date().toISOString(),
            referenceCode: 'IMPORTACAO-EXCEL',
            notes: 'Saldo inicial importado via planilha Excel.',
          });
        }
      }
      await loadAll();
      setImportPreviewRows([]);
      showFeedback(`${importPreviewRows.length} linha(s) importada(s) com sucesso.`);
    } catch (err) {
      handleError(resolveErrorMessage(err));
    } finally {
      setImportingStock(false);
    }
  }, [handleError, importPreviewRows, loadAll, showFeedback]);

  if (!canView) {
    return (
      <div className="p-8">
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6 text-amber-700">
          <div className="flex items-center gap-3 text-lg font-semibold">
            <FaExclamationTriangle />
            <span>Acesso restrito</span>
          </div>
          <p className="mt-3 text-sm text-amber-700">
            Você não possui permissão para visualizar o módulo de estoque.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="inventory-page space-y-8 p-6">
      {bulkUpdateMessage && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/45 backdrop-blur-sm">
          <div className="rounded-3xl border border-indigo-100 bg-white p-6 text-center shadow-2xl">
            <FaSpinner className="mx-auto mb-3 animate-spin text-4xl text-indigo-600" />
            <p className="text-base font-semibold text-slate-800">{bulkUpdateMessage}</p>
            <p className="mt-1 text-sm text-slate-500">Sincronizando vínculos e atualizando os cadastros em estoque.</p>
          </div>
        </div>
      )}
      <header className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="inline-flex items-center gap-3 rounded-2xl bg-slate-900 px-5 py-3 text-2xl font-semibold text-white shadow">
            <FaBoxes className="text-white" />
            <span>Controle de Estoque</span>
          </h1>
        </div>
        <div className="inventory-actions flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={loadAll}
            className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 transition hover:border-indigo-200 hover:text-indigo-600"
          >
            <FaSyncAlt className="text-xs" /> Atualizar
          </button>
          {canManage && (
            <button
              type="button"
              onClick={() => setImportModalOpen(true)}
              className="inline-flex items-center gap-2 rounded-full border border-sky-200 bg-sky-50 px-4 py-2 text-sm font-medium text-sky-700 transition hover:border-sky-300 hover:bg-sky-100"
            >
              <FaFileImport /> Importar planilha
            </button>
          )}
          {canManage && (
            <button
              type="button"
              onClick={() => openItemModal(null)}
              className="inline-flex items-center gap-2 rounded-full bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow transition hover:bg-indigo-500"
            >
              <FaPlus /> Novo item
            </button>
          )}
        </div>
      </header>

      {(feedback || error) && (
        <div
          className={`rounded-2xl border p-4 text-sm ${
            feedback
              ? feedbackType === 'success'
                ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                : 'border-amber-200 bg-amber-50 text-amber-700'
              : 'border-rose-200 bg-rose-50 text-rose-700'
          }`}
        >
          <div className="flex items-center gap-2 font-medium">
            {feedback ? <FaCheckCircle /> : <FaExclamationTriangle />}
            <span>{feedback || error}</span>
          </div>
        </div>
      )}




      <nav className="inventory-tabs flex flex-wrap gap-2 rounded-2xl border border-slate-200 bg-white p-2 shadow-sm">
        {[{ key: 'items', label: 'Itens e movimentações', icon: FaBoxes }, { key: 'dashboard', label: 'Dashboard', icon: FaChartPie }, { key: 'history', label: 'Histórico de alterações', icon: FaHistory }, { key: 'ai', label: 'Relatório com análise de IA', icon: FaRobot }, { key: 'registrations', label: 'Cadastros', icon: FaWarehouse }].map((tab) => { const Icon = tab.icon; return <button key={tab.key} type="button" onClick={() => setActiveTab(tab.key)} disabled={tab.key === 'ai' && !aiSettings.configured} className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold transition ${activeTab === tab.key ? 'bg-indigo-600 text-white shadow' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'} disabled:cursor-not-allowed disabled:opacity-50`}><Icon /> {tab.label}</button>; })}
        {!aiSettings.loading && !aiSettings.configured ? <span className="px-3 py-2 text-xs font-medium text-amber-600">Relatório de IA disponível após configurar ChatGPT em Automações e IA.</span> : null}
      </nav>



      {activeTab === 'dashboard' && (
        <>
      <section>
        <h2 className="text-lg font-semibold text-slate-700">Visão geral</h2>
        <div className="mt-4 grid gap-4 md:grid-cols-4">
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <p className="text-sm text-slate-500">Itens cadastrados</p>
            <p className="mt-2 text-2xl font-semibold text-slate-800">
              {items.length}
            </p>
          </div>
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 shadow-sm">
            <p className="flex items-center gap-2 text-sm font-medium text-amber-700">
              <FaExclamationTriangle /> Itens em alerta
            </p>
            <p className="mt-2 text-2xl font-semibold text-amber-700">
              {lowStockItems.length}
            </p>
          </div>
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 shadow-sm">
            <p className="text-sm font-medium text-emerald-700">Valor total</p>
            <p className="mt-2 text-2xl font-semibold text-emerald-700">
              {formatCurrency(totalInventoryValue)}
            </p>
          </div>
          <div className="rounded-2xl border border-indigo-200 bg-indigo-50 p-4 shadow-sm">
            <p className="text-sm font-medium text-indigo-700">Saídas (último período)</p>
            <p className="mt-2 text-2xl font-semibold text-indigo-700">
              {formatCurrency(recentOutboundValue)}
            </p>
          </div>
        </div>
      </section>
        <section className="grid gap-6 lg:grid-cols-2">
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="flex items-center gap-2 text-lg font-semibold text-slate-700"><FaChartPie className="text-indigo-500" /> Valores por depósito</h2>
            <div className="mt-4 space-y-3">
              {warehouseDashboard.map((warehouse) => {
                const max = Math.max(...warehouseDashboard.map((entry) => entry.value), 1);
                return <div key={warehouse.id ?? warehouse.name} className="rounded-xl bg-slate-50 p-3"><div className="flex items-center justify-between text-sm"><span className="font-semibold text-slate-700">{warehouse.name}</span><span className="font-semibold text-emerald-700">{formatCurrency(warehouse.value)}</span></div><div className="mt-2 h-2 rounded-full bg-slate-200"><div className="h-2 rounded-full bg-indigo-500" style={{ width: `${Math.max(4, (warehouse.value / max) * 100)}%` }} /></div><p className="mt-1 text-xs text-slate-500">{warehouse.itemCount} item(ns) • {warehouse.quantity} unidades</p></div>;
              })}
            </div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="flex items-center gap-2 text-lg font-semibold text-slate-700"><FaChartPie className="text-emerald-500" /> Valores por categoria</h2>
            <div className="mt-4 space-y-3">
              {categoryDashboard.map((category) => {
                const max = Math.max(...categoryDashboard.map((entry) => entry.value), 1);
                return <div key={category.name} className="rounded-xl bg-slate-50 p-3"><div className="flex items-center justify-between text-sm"><span className="font-semibold text-slate-700">{category.name}</span><span className="font-semibold text-emerald-700">{formatCurrency(category.value)}</span></div><div className="mt-2 h-2 rounded-full bg-slate-200"><div className="h-2 rounded-full bg-emerald-500" style={{ width: `${Math.max(4, (category.value / max) * 100)}%` }} /></div><p className="mt-1 text-xs text-slate-500">{category.itemCount} item(ns) • {category.quantity} unidades</p></div>;
              })}
            </div>
          </div>
        </section>
        </>
      )}

      {activeTab === 'ai' && aiSettings.configured && (
        <AIInsightsPanel
          module="inventory"
          title="Insights inteligentes do estoque"
          subtitle="Análise executiva única do estoque, cruzando saldos, movimentações, níveis mínimos, depósitos, categorias, valor e sinais de ruptura ou excesso."
          data={{ items, warehouses, categories, movements, lowStockItems, totalInventoryValue, warehouseDashboard, categoryDashboard, recentOutboundValue }}
        />
      )}

      {activeTab === 'history' && (
        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"><h2 className="flex items-center gap-2 text-lg font-semibold text-slate-700"><FaHistory className="text-indigo-500" /> Histórico de alterações no depósito</h2><div className="mt-4 space-y-3">{filteredMovements.length === 0 ? <p className="text-sm text-slate-500">Nenhuma alteração registrada para os filtros atuais.</p> : filteredMovements.map((movement) => <div key={movement.id ?? `${movement.type}-${movement.occurredAt}`} className="rounded-xl border border-slate-100 bg-slate-50 p-3 text-sm"><div className="flex justify-between gap-3"><strong>{MOVEMENT_TYPES.find((type) => type.value === movement.type)?.label || 'Movimentação'} • {movement.item.name}</strong><span className="text-slate-500">{formatDateTime(movement.occurredAt)}</span></div><p className="mt-1 text-slate-600">{movement.quantity} {movement.item.unit} • Origem: {movement.originWarehouse?.name || '—'} • Destino: {movement.targetWarehouse?.name || '—'} • {movement.notes || movement.referenceCode || 'Sem observações'}</p></div>)}</div></section>
      )}

      {activeTab === 'items' && (
        <>
      <section className="rounded-2xl border border-indigo-100 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap items-center gap-3">
            <FaWarehouse className="text-xl text-indigo-500" />
          <button type="button" onClick={() => { setFilters((prev) => ({ ...prev, warehouseId: 'all' })); setMovementFilters((prev) => ({ ...prev, warehouseId: 'all' })); setAiFilters((prev) => ({ ...prev, warehouseId: 'all' })); }} className={`rounded-full px-4 py-2 text-sm font-medium ${filters.warehouseId === 'all' ? 'bg-indigo-600 text-white shadow' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>Todos os depósitos</button>
          {warehouses.map((warehouse) => (
            <button key={warehouse.id ?? warehouse.name} type="button" onClick={() => { const value = String(warehouse.id ?? warehouse.name); setFilters((prev) => ({ ...prev, warehouseId: value })); setMovementFilters((prev) => ({ ...prev, warehouseId: value })); setAiFilters((prev) => ({ ...prev, warehouseId: value })); }} className={`rounded-full px-4 py-2 text-sm font-medium ${String(filters.warehouseId) === String(warehouse.id ?? warehouse.name) ? 'bg-indigo-600 text-white shadow' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>{warehouse.name}</button>
          ))}
          </div>
          <button type="button" onClick={saveWarehousePreference} className="inline-flex items-center gap-2 rounded-full bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-indigo-500"><FaSave /> Salvar preferência</button>
        </div>
      </section>
      {canManage && importPreviewRows.length > 0 && (
        <section ref={importPreviewRef} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="text-base font-semibold text-slate-800">Prévia antes de inserir</h3>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">
                {importPreviewRows.length} linha(s)
              </span>
              <button
                type="button"
                onClick={handleConfirmImport}
                disabled={!importPreviewRows.length || importingStock}
                className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-slate-300"
              >
                {importingStock ? 'Importando…' : 'Confirmar importação'}
              </button>
            </div>
          </div>
          <div className="mt-4 overflow-x-auto rounded-xl border border-slate-200">
            <table className="min-w-[980px] divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50 text-xs font-medium uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-3 text-left">Linha</th>
                  <th className="px-4 py-3 text-left">Item</th>
                  <th className="px-4 py-3 text-left">SKU</th>
                  <th className="px-4 py-3 text-left">Categoria</th>
                  <th className="px-4 py-3 text-left">Depósito</th>
                  <th className="px-4 py-3 text-right">Quantidade</th>
                  <th className="px-4 py-3 text-left">Pendências</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {importPreviewRows.map((row) => (
                    <tr key={row.rowNumber} className={row.errors.length ? 'bg-rose-50/60' : ''}>
                      <td className="px-4 py-3 text-slate-600">{row.rowNumber}</td>
                      <td className="px-4 py-3 text-slate-700">{row.payload.name || '—'}</td>
                      <td className="px-4 py-3 text-slate-700">{row.payload.sku || '—'}</td>
                      <td className="px-4 py-3 text-slate-700">{row.payload.category || '—'}</td>
                      <td className="px-4 py-3 text-slate-700">{row.payload.warehouseName || row.payload.warehouse || '—'}</td>
                      <td className="px-4 py-3 text-right text-slate-700">
                        {row.payload.quantity ?? '—'} {row.payload.unit || 'un'}
                      </td>
                      <td className="px-4 py-3 text-xs text-rose-700">
                        {row.errors.length ? row.errors.join(' ') : 'Sem pendências'}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <section className="space-y-4">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <h2 className="text-lg font-semibold text-slate-700">Itens em estoque</h2>
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative">
              <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="search"
                value={filters.search}
                onChange={(event) =>
                  setFilters((prev) => ({ ...prev, search: event.target.value }))
                }
                placeholder="Buscar por nome, SKU ou categoria"
                className="w-72 rounded-full border border-slate-200 py-2 pl-10 pr-4 text-sm text-slate-700 shadow-sm focus:border-indigo-300 focus:outline-none"
              />
            </div>
            <select
              value={filters.warehouseId}
              onChange={(event) =>
                setFilters((prev) => ({ ...prev, warehouseId: event.target.value }))
              }
              className="rounded-full border border-slate-200 px-4 py-2 text-sm text-slate-600 shadow-sm focus:border-indigo-300 focus:outline-none"
            >
              <option value="all">Todos os depósitos</option>
              {warehouses.map((warehouse) => (
                <option
                  key={warehouse.id ?? warehouse.name}
                  value={warehouse.id ?? warehouse.name}
                >
                  {warehouse.name}
                </option>
              ))}
            </select>
            <select
              value={filters.categoryId}
              onChange={(event) =>
                setFilters((prev) => ({ ...prev, categoryId: event.target.value }))
              }
              className="rounded-full border border-slate-200 px-4 py-2 text-sm text-slate-600 shadow-sm focus:border-indigo-300 focus:outline-none"
            >
              <option value="all">Todas as categorias</option>
              {categories.map((category) => (
                <option
                  key={category.id ?? category.name}
                  value={category.id ?? category.name}
                >
                  {category.name}
                </option>
              ))}
            </select>
            <select
              value={filters.status}
              onChange={(event) =>
                setFilters((prev) => ({ ...prev, status: event.target.value }))
              }
              className="rounded-full border border-slate-200 px-4 py-2 text-sm text-slate-600 shadow-sm focus:border-indigo-300 focus:outline-none"
            >
              <option value="all">Todos os status</option>
              <option value="ok">Saudável</option>
              <option value="low">Abaixo do mínimo</option>
              <option value="excess">Acima do máximo</option>
              <option value="inactive">Inativo</option>
            </select>
            <select
              value={reportFormat}
              onChange={(event) => setReportFormat(event.target.value)}
              className="rounded-full border border-slate-200 px-4 py-2 text-sm text-slate-600 shadow-sm focus:border-indigo-300 focus:outline-none"
            >
              <option value="pdf">PDF</option>
              <option value="xlsx">Excel (.xlsx)</option>
              <option value="xls">Excel (.xls)</option>
            </select>
            <button
              type="button"
              onClick={handleDownloadReport}
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 transition hover:border-indigo-200 hover:text-indigo-600"
            >
              <FaDownload /> Exportar
            </button>
          </div>
        </div>

        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="inventory-items-table-scroll overflow-x-auto">
            <table className="inventory-items-table min-w-full divide-y divide-slate-100 text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-3 text-left">Produto</th>
                  <th className="px-4 py-3 text-left">Categoria</th>
                  <th className="px-4 py-3 text-left">Depósitos</th>
                  <th className="px-4 py-3 text-right">Estoque</th>
                  <th className="px-4 py-3 text-right">Mínimo</th>
                  <th className="px-4 py-3 text-right">Máximo</th>
                  <th className="px-4 py-3 text-right">Custo</th>
                  <th className="px-4 py-3 text-right">Validade</th>
                  <th className="px-4 py-3 text-right">Atualizado</th>
                  {canManage && <th className="inventory-items-actions-header px-4 py-3 text-right">Ações</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  <tr>
                    <td
                      colSpan={canManage ? 9 : 8}
                      className="px-4 py-6 text-center text-slate-500"
                    >
                      Carregando informações de estoque...
                    </td>
                  </tr>
                ) : filteredItems.length === 0 ? (
                  <tr>
                    <td
                      colSpan={canManage ? 9 : 8}
                      className="px-4 py-6 text-center text-slate-500"
                    >
                      Nenhum item encontrado para os filtros selecionados.
                    </td>
                  </tr>
                ) : (
                  paginatedItems.map((item) => {
                    const statusBadge = (() => {
                      if (!item.active) {
                        return 'bg-slate-100 text-slate-500';
                      }
                      if (item.minimumStock && item.stock <= item.minimumStock) {
                        return 'bg-amber-100 text-amber-700';
                      }
                      if (item.maximumStock && item.stock > item.maximumStock) {
                        return 'bg-indigo-100 text-indigo-700';
                      }
                      return 'bg-emerald-100 text-emerald-700';
                    })();

                    return (
                      <tr
                        key={item.id ?? item.sku}
                        className="hover:bg-slate-50/60"
                      >
                        <td className="px-4 py-3">
                          <div className="flex flex-col">
                            <span className="font-medium text-slate-800">
                              {item.name}
                            </span>
                            <span className="text-xs text-slate-400">
                              {item.sku || 'Sem SKU'}
                            </span>
                            <span
                              className={`mt-1 inline-flex w-fit items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${statusBadge}`}
                            >
                              {item.active
                                ? item.minimumStock && item.stock <= item.minimumStock
                                  ? 'Atenção: abaixo do mínimo'
                                  : item.maximumStock && item.stock > item.maximumStock
                                  ? 'Acima do máximo'
                                  : 'Estoque saudável'
                                : 'Inativo'}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-slate-600">
                          {item.category?.name || 'Sem categoria'}
                        </td>
                        <td className="px-4 py-3 text-slate-600">
                          {Array.isArray(item.warehouses) && item.warehouses.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {item.warehouses.map((entry) => (
                                <span
                                  key={`${item.id ?? item.sku}-${entry.id ?? entry.name}`}
                                  className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600"
                                >
                                  <span className="font-medium">
                                    {entry.name || 'Sem depósito'}
                                  </span>
                                  <span className="text-slate-500">
                                    {Number(entry.quantity || 0)} {item.unit}
                                  </span>
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-slate-400">Sem depósito</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right font-semibold text-slate-800">
                          {item.stock} {item.unit}
                        </td>
                        <td className="px-4 py-3 text-right text-slate-500">
                          {item.minimumStock || '-'}
                        </td>
                        <td className="px-4 py-3 text-right text-slate-500">
                          {item.maximumStock || '-'}
                        </td>
                        <td className="px-4 py-3 text-right text-slate-600">
                          {formatCurrency(item.costPrice)}
                        </td>
                        <td className="px-4 py-3 text-right text-slate-500">
                          {item.expirationDate
                            ? formatDate(item.expirationDate)
                            : '-'}
                        </td>
                        <td className="px-4 py-3 text-right text-slate-500">
                          {formatDateTime(item.lastMovementAt || item.updatedAt)}
                        </td>
                        {canManage && (
                          <td className="inventory-items-actions-cell px-4 py-3 text-right">
                            <div className="inventory-item-actions flex justify-end gap-2">
                              <button
                                type="button"
                                className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1 text-xs font-medium text-slate-600 transition hover:border-indigo-200 hover:text-indigo-600"
                                onClick={() => openMovementModal(item)}
                              >
                                <FaExchangeAlt /> Movimentar
                              </button>
                              <button
                                type="button"
                                className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1 text-xs font-medium text-slate-600 transition hover:border-indigo-200 hover:text-indigo-600"
                                onClick={() => openItemModal(item)}
                              >
                                <FaEdit /> Editar
                              </button>
                              <button
                                type="button"
                                className="inline-flex items-center gap-1 rounded-full border border-rose-200 px-3 py-1 text-xs font-medium text-rose-600 transition hover:bg-rose-50"
                                onClick={() => handleDeleteItem(item)}
                              >
                                <FaTrash /> Excluir
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
        <div
          className="inventory-pagination mt-4 flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-3 text-sm text-slate-600 sm:flex-row sm:items-center sm:justify-between"
          aria-label="Paginação da consulta de estoque"
        >
          <span aria-live="polite">
            Exibindo {itemsPagination.start}–{itemsPagination.end} de {itemsPagination.total} item(ns)
          </span>
          <div className="flex flex-wrap items-center gap-2">
            <label htmlFor="inventory-items-page-size" className="text-xs font-medium text-slate-500">
              Itens por página
            </label>
            <select
              id="inventory-items-page-size"
              value={itemsPageSize}
              onChange={(event) => setItemsPageSize(Number(event.target.value))}
              className="min-h-[44px] rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700"
            >
              {TABLE_PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => setItemsPage(itemsPagination.page - 1)}
              disabled={itemsPagination.page === 1}
              className="min-h-[44px] rounded-lg border border-slate-200 px-3 py-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Anterior
            </button>
            <span className="min-w-24 text-center">
              Página {itemsPagination.page} de {itemsPagination.totalPages}
            </span>
            <button
              type="button"
              onClick={() => setItemsPage(itemsPagination.page + 1)}
              disabled={itemsPagination.page === itemsPagination.totalPages}
              className="min-h-[44px] rounded-lg border border-slate-200 px-3 py-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Próxima
            </button>
          </div>
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <h2 className="flex items-center gap-2 text-lg font-semibold text-slate-700">
              <FaClipboardList className="text-indigo-500" /> Movimentações recentes
            </h2>
            <div className="flex flex-wrap items-center gap-3">
              <select
                value={movementFilters.type}
                onChange={(event) =>
                  setMovementFilters((prev) => ({
                    ...prev,
                    type: event.target.value,
                  }))
                }
                className="rounded-full border border-slate-200 px-4 py-2 text-sm text-slate-600 shadow-sm focus:border-indigo-300 focus:outline-none"
              >
                <option value="all">Todos os tipos</option>
                {MOVEMENT_TYPES.map((movement) => (
                  <option key={movement.value} value={movement.value}>
                    {movement.label}
                  </option>
                ))}
              </select>
              <select
                value={movementFilters.warehouseId}
                onChange={(event) =>
                  setMovementFilters((prev) => ({
                    ...prev,
                    warehouseId: event.target.value,
                  }))
                }
                className="rounded-full border border-slate-200 px-4 py-2 text-sm text-slate-600 shadow-sm focus:border-indigo-300 focus:outline-none"
              >
                <option value="all">Todos os depósitos</option>
                {warehouses.map((warehouse) => (
                  <option
                    key={warehouse.id ?? warehouse.name}
                    value={warehouse.id ?? warehouse.name}
                  >
                    {warehouse.name}
                  </option>
                ))}
              </select>
              <select
                value={movementFilters.period}
                onChange={(event) =>
                  setMovementFilters((prev) => ({
                    ...prev,
                    period: event.target.value,
                  }))
                }
                className="rounded-full border border-slate-200 px-4 py-2 text-sm text-slate-600 shadow-sm focus:border-indigo-300 focus:outline-none"
              >
                <option value="7">Últimos 7 dias</option>
                <option value="30">Últimos 30 dias</option>
                <option value="90">Últimos 90 dias</option>
                <option value="365">Últimos 12 meses</option>
                <option value="0">Todo o histórico</option>
              </select>
            </div>
          </div>

          <div className="mt-4 space-y-3">
            {filteredMovements.length === 0 ? (
              <div className="rounded-2xl border border-slate-200 bg-white p-6 text-center text-sm text-slate-500">
                Nenhuma movimentação registrada no período selecionado.
              </div>
            ) : (
              paginatedMovements.map((movement) => {
                const typeInfo = MOVEMENT_TYPES.find(
                  (type) => type.value === movement.type,
                );
                const TypeIcon = typeInfo?.icon ?? FaExchangeAlt;
                const isInbound = movement.type === 'INBOUND';
                const isOutbound = movement.type === 'OUTBOUND';
                const colorClass = isInbound
                  ? 'text-emerald-600'
                  : isOutbound
                  ? 'text-rose-600'
                  : 'text-indigo-600';

                return (
                  <div
                    key={movement.id ?? `${movement.type}-${movement.referenceCode}`}
                    className="flex items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
                  >
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
                        <TypeIcon className={colorClass} />
                        <span>
                          {typeInfo?.label ?? 'Movimentação'} • {movement.item.name}
                        </span>
                      </div>
                      <div className="text-xs text-slate-500">
                        {movement.originWarehouse?.name && (
                          <span>
                            Origem: {movement.originWarehouse.name}
                            {' · '}
                          </span>
                        )}
                        {movement.targetWarehouse?.name && (
                          <span>
                            Destino: {movement.targetWarehouse.name}
                            {' · '}
                          </span>
                        )}
                        <span>{movement.referenceCode || 'Sem referência'}</span>
                      </div>
                    </div>
                    <div className="text-right text-sm">
                      <p className={`font-semibold ${colorClass}`}>
                        {movement.quantity} {movement.item.unit}
                      </p>
                      <p className="text-xs text-slate-400">
                        {formatDateTime(movement.occurredAt)}
                      </p>
                    </div>
                  </div>
                );
              })
            )}
          </div>
          <div
            className="inventory-movements-pagination mt-4 flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-3 text-sm text-slate-600 sm:flex-row sm:items-center sm:justify-between"
            aria-label="Paginação das movimentações de estoque"
          >
            <span aria-live="polite">
              Exibindo {movementsPagination.start}–{movementsPagination.end} de {movementsPagination.total} movimentação(ões)
            </span>
            <div className="flex flex-wrap items-center gap-2">
              <label htmlFor="inventory-movements-page-size" className="text-xs font-medium text-slate-500">
                Itens por página
              </label>
              <select
                id="inventory-movements-page-size"
                value={movementsPageSize}
                onChange={(event) => setMovementsPageSize(Number(event.target.value))}
                className="min-h-[44px] rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700"
              >
                {TABLE_PAGE_SIZE_OPTIONS.map((size) => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => setMovementsPage(movementsPagination.page - 1)}
                disabled={movementsPagination.page === 1}
                className="min-h-[44px] rounded-lg border border-slate-200 px-3 py-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Anterior
              </button>
              <span className="min-w-24 text-center">
                Página {movementsPagination.page} de {movementsPagination.totalPages}
              </span>
              <button
                type="button"
                onClick={() => setMovementsPage(movementsPagination.page + 1)}
                disabled={movementsPagination.page === movementsPagination.totalPages}
                className="min-h-[44px] rounded-lg border border-slate-200 px-3 py-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Próxima
              </button>
            </div>
          </div>
        </div>
      </section>

        </>
      )}

      {activeTab === 'registrations' && (
        <section className="grid gap-6 lg:grid-cols-2">
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="flex items-center gap-2 text-base font-semibold text-slate-700">
                <FaWarehouse className="text-indigo-500" /> Depósitos
              </h3>
              {canManage && (
                <button
                  type="button"
                  onClick={() => openWarehouseModal(null)}
                  className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-3 py-1 text-xs font-medium text-slate-600 transition hover:border-indigo-200 hover:text-indigo-600"
                >
                  <FaPlus /> Novo depósito
                </button>
              )}
            </div>
            <ul className="space-y-3 text-sm">
              {warehouses.length === 0 ? (
                <li className="text-slate-500">Nenhum depósito cadastrado.</li>
              ) : (
                warehouses.map((warehouse) => (
                  <li
                    key={warehouse.id ?? warehouse.name}
                    className="flex items-start justify-between gap-3 rounded-xl border border-slate-100 bg-slate-50 p-3"
                  >
                    <div>
                      <p className="font-semibold text-slate-700">
                        {warehouse.name}
                      </p>
                      <p className="text-xs text-slate-500">
                        {warehouse.code ? `Código: ${warehouse.code} · ` : ''}
                        {warehouse.address || 'Sem endereço informado'}
                      </p>
                      {warehouse.manager && (
                        <p className="text-xs text-slate-400">
                          Responsável: {warehouse.manager}
                        </p>
                      )}
                    </div>
                    {canManage && (
                      <div className="flex flex-col gap-2 text-xs">
                        <button
                          type="button"
                          onClick={() => openWarehouseModal(warehouse)}
                          className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1 font-medium text-slate-600 transition hover:border-indigo-200 hover:text-indigo-600"
                        >
                          <FaEdit /> Editar
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteWarehouse(warehouse)}
                          className="inline-flex items-center gap-1 rounded-full border border-rose-200 px-3 py-1 font-medium text-rose-600 transition hover:bg-rose-50"
                        >
                          <FaTrash /> Excluir
                        </button>
                      </div>
                    )}
                  </li>
                ))
              )}
            </ul>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-base font-semibold text-slate-700">Categorias</h3>
              {canManage && (
                <button
                  type="button"
                  onClick={() => openCategoryModal(null)}
                  className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-3 py-1 text-xs font-medium text-slate-600 transition hover:border-indigo-200 hover:text-indigo-600"
                >
                  <FaPlus /> Nova categoria
                </button>
              )}
            </div>
            <ul className="space-y-3 text-sm">
              {categories.length === 0 ? (
                <li className="text-slate-500">Nenhuma categoria cadastrada.</li>
              ) : (
                categories.map((category) => (
                  <li
                    key={category.id ?? category.name}
                    className="flex items-start justify-between gap-3 rounded-xl border border-slate-100 bg-slate-50 p-3"
                  >
                    <div>
                      <p className="font-semibold text-slate-700">
                        {category.name}
                      </p>
                      {category.description && (
                        <p className="text-xs text-slate-500">
                          {category.description}
                        </p>
                      )}
                    </div>
                    {canManage && (
                      <div className="flex flex-col gap-2 text-xs">
                        <button
                          type="button"
                          onClick={() => openCategoryModal(category)}
                          className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1 font-medium text-slate-600 transition hover:border-indigo-200 hover:text-indigo-600"
                        >
                          <FaEdit /> Editar
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteCategory(category)}
                          className="inline-flex items-center gap-1 rounded-full border border-rose-200 px-3 py-1 font-medium text-rose-600 transition hover:bg-rose-50"
                        >
                          <FaTrash /> Excluir
                        </button>
                      </div>
                    )}
                  </li>
                ))
              )}
            </ul>
          </div>
        </section>
      )}

      <Modal
        open={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        size="max-w-2xl"
        showCloseButton={false}
      >
        <div className="space-y-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-slate-800">
                Importação via planilha (Excel)
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                Carregue uma planilha para validar os dados e enviar para a prévia antes de inserir no estoque.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setImportModalOpen(false)}
              className="text-slate-400 transition hover:text-slate-600"
              aria-label="Fechar"
            >
              <FaTimes />
            </button>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <ol className="space-y-2 text-sm text-slate-600">
              <li><strong>1.</strong> Baixe o template padrão e preencha os dados (colunas com <strong>*</strong> são obrigatórias).</li>
              <li><strong>2.</strong> Use as opções da aba <strong>Opcoes</strong> para Categorias e Depósitos.</li>
              <li><strong>3.</strong> Selecione o arquivo para carregar a prévia automaticamente.</li>
              <li><strong>4.</strong> Confira as linhas na tela de prévia antes de confirmar a importação.</li>
            </ol>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={handleDownloadImportTemplate}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 shadow-sm transition hover:border-slate-300 hover:text-slate-800"
            >
              <FaDownload />
              Baixar template
            </button>
            <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-sky-700">
              <FaFileImport />
              Selecionar planilha
              <input type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={handleImportFileChange} />
            </label>
          </div>
        </div>
      </Modal>

      <Modal
        open={itemModalOpen}
        onClose={() => setItemModalOpen(false)}
        size="max-w-4xl"
        showCloseButton={false}
        panelClassName="inventory-item-modal-panel"
      >
        <form className="inventory-item-form" onSubmit={handleSaveItem}>
          <div className="inventory-modal-heading">
            <div className="inventory-modal-heading__identity">
              <span className="inventory-modal-heading__icon inventory-modal-heading__icon--item">
                <FaBoxes />
              </span>
              <div>
                <h3>{itemForm.id ? 'Editar item' : 'Novo item'}</h3>
                <p>
                  {itemForm.id
                    ? 'Atualize os dados, limites e controles deste item.'
                    : 'Cadastre o item e depois registre a entrada pelo botão Movimentar.'}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setItemModalOpen(false)}
              className="inventory-modal-close"
              aria-label="Fechar"
            >
              <FaTimes />
            </button>
          </div>

          <section className="inventory-form-section">
            <div className="inventory-form-section__heading">
              <span><FaBoxes /></span>
              <div>
                <strong>Identificação</strong>
                <small>Informações principais para localizar o item rapidamente.</small>
              </div>
            </div>
            <div className="inventory-form-grid">
              <label className="inventory-field inventory-field--wide">
                <span>Nome do produto <b>*</b></span>
                <input
                  type="text"
                  required
                  value={itemForm.name}
                  placeholder="Ex.: Papel A4, Toner HP, Café..."
                  onChange={(event) =>
                    setItemForm((prev) => ({ ...prev, name: event.target.value }))
                  }
                />
              </label>
              <label className="inventory-field">
                <span>SKU / Código interno</span>
                <input
                  type="text"
                  value={itemForm.sku}
                  placeholder="Ex.: EST-001"
                  onChange={(event) =>
                    setItemForm((prev) => ({ ...prev, sku: event.target.value }))
                  }
                />
              </label>
              <label className="inventory-field">
                <span>Categoria</span>
                <select
                  value={itemForm.categoryId}
                  onChange={(event) =>
                    setItemForm((prev) => ({
                      ...prev,
                      categoryId: event.target.value,
                    }))
                  }
                >
                  <option value="">Sem categoria</option>
                  {categories.map((category) => (
                    <option
                      key={category.id ?? category.name}
                      value={category.id ?? category.name}
                    >
                      {category.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="inventory-field">
                <span>Unidade de medida</span>
                <input
                  type="text"
                  value={itemForm.unit}
                  placeholder="un, cx, kg, l..."
                  onChange={(event) =>
                    setItemForm((prev) => ({ ...prev, unit: event.target.value }))
                  }
                />
              </label>
            </div>
          </section>

          <section className="inventory-form-section">
            <div className="inventory-form-section__heading">
              <span><FaWarehouse /></span>
              <div>
                <strong>Armazenamento e controle</strong>
                <small>Defina onde o item fica e os limites que ajudam nos alertas.</small>
              </div>
            </div>
            <div className="inventory-form-grid">
              <label className="inventory-field inventory-field--wide">
                <span>Depósito padrão</span>
                <select
                  value={itemForm.warehouseId}
                  onChange={(event) =>
                    setItemForm((prev) => ({
                      ...prev,
                      warehouseId: event.target.value,
                    }))
                  }
                >
                  <option value="">Selecione um depósito</option>
                  {warehouses.map((warehouse) => (
                    <option
                      key={warehouse.id ?? warehouse.name}
                      value={warehouse.id ?? warehouse.name}
                    >
                      {warehouse.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="inventory-field">
                <span>Estoque mínimo</span>
                <input
                  type="number"
                  min="0"
                  value={itemForm.minimumStock}
                  placeholder="0"
                  onChange={(event) =>
                    setItemForm((prev) => ({
                      ...prev,
                      minimumStock: event.target.value,
                    }))
                  }
                />
              </label>
              <label className="inventory-field">
                <span>Estoque máximo</span>
                <input
                  type="number"
                  min="0"
                  value={itemForm.maximumStock}
                  placeholder="0"
                  onChange={(event) =>
                    setItemForm((prev) => ({
                      ...prev,
                      maximumStock: event.target.value,
                    }))
                  }
                />
              </label>
              <label className="inventory-field">
                <span>Estoque de segurança</span>
                <input
                  type="number"
                  min="0"
                  value={itemForm.safetyStock}
                  placeholder="0"
                  onChange={(event) =>
                    setItemForm((prev) => ({
                      ...prev,
                      safetyStock: event.target.value,
                    }))
                  }
                />
              </label>
              <label className="inventory-field">
                <span>Validade</span>
                <input
                  type="date"
                  value={itemForm.expirationDate}
                  onChange={(event) =>
                    setItemForm((prev) => ({
                      ...prev,
                      expirationDate: event.target.value,
                    }))
                  }
                  disabled={!itemForm.trackExpiration}
                />
              </label>
            </div>

            <div className="inventory-option-grid">
              <label className={`inventory-option-card ${itemForm.trackBatch ? 'is-active' : ''}`}>
                <input
                  type="checkbox"
                  checked={itemForm.trackBatch}
                  onChange={(event) =>
                    setItemForm((prev) => ({
                      ...prev,
                      trackBatch: event.target.checked,
                    }))
                  }
                />
                <span className="inventory-option-card__icon"><FaClipboardList /></span>
                <span>
                  <strong>Controlar lote</strong>
                  <small>Ative se o material precisa de identificação por lote.</small>
                </span>
              </label>
              <label className={`inventory-option-card ${itemForm.trackExpiration ? 'is-active' : ''}`}>
                <input
                  type="checkbox"
                  checked={itemForm.trackExpiration}
                  onChange={(event) =>
                    setItemForm((prev) => ({
                      ...prev,
                      trackExpiration: event.target.checked,
                      expirationDate: event.target.checked
                        ? prev.expirationDate
                        : '',
                    }))
                  }
                />
                <span className="inventory-option-card__icon"><FaHistory /></span>
                <span>
                  <strong>Controlar validade</strong>
                  <small>Habilita o acompanhamento da data de validade.</small>
                </span>
              </label>
              <label className={`inventory-option-card ${itemForm.active ? 'is-active' : ''}`}>
                <input
                  type="checkbox"
                  checked={itemForm.active}
                  onChange={(event) =>
                    setItemForm((prev) => ({ ...prev, active: event.target.checked }))
                  }
                />
                <span className="inventory-option-card__icon"><FaCheckCircle /></span>
                <span>
                  <strong>Item ativo</strong>
                  <small>Itens inativos permanecem no histórico, mas ficam identificados.</small>
                </span>
              </label>
            </div>
          </section>

          <section className="inventory-form-section">
            <div className="inventory-form-section__heading">
              <span><FaChartPie /></span>
              <div>
                <strong>Valores</strong>
                <small>Informações financeiras usadas nos relatórios de estoque.</small>
              </div>
            </div>
            <div className="inventory-form-grid inventory-form-grid--two">
              <label className="inventory-field">
                <span>Custo unitário (R$)</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={itemForm.costPrice}
                  placeholder="0,00"
                  onChange={(event) =>
                    setItemForm((prev) => ({ ...prev, costPrice: event.target.value }))
                  }
                />
              </label>
              <label className="inventory-field">
                <span>Preço de venda (R$)</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={itemForm.salePrice}
                  placeholder="0,00"
                  onChange={(event) =>
                    setItemForm((prev) => ({ ...prev, salePrice: event.target.value }))
                  }
                />
              </label>
            </div>
          </section>

          <section className="inventory-form-section">
            <div className="inventory-form-section__heading">
              <span><FaClipboardList /></span>
              <div>
                <strong>Observações</strong>
                <small>Use apenas quando houver alguma informação relevante sobre o item.</small>
              </div>
            </div>
            <label className="inventory-field">
              <textarea
                value={itemForm.description}
                onChange={(event) =>
                  setItemForm((prev) => ({ ...prev, description: event.target.value }))
                }
                rows={3}
                placeholder="Descrição, especificação, observações de compra..."
              />
            </label>
          </section>

          {!itemForm.id && (
            <div className="inventory-info-callout">
              <FaLightbulb />
              <span>
                <strong>Saldo inicial:</strong> salve o item e use <b>Movimentar → Entrada</b> para registrar a quantidade disponível.
              </span>
            </div>
          )}

          <div className="inventory-modal-actions">
            <button
              type="button"
              onClick={() => setItemModalOpen(false)}
              className="inventory-secondary-button"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={savingItem}
              className="inventory-primary-button"
            >
              {savingItem ? <FaSpinner className="animate-spin" /> : <FaSave />}
              {savingItem ? 'Salvando...' : itemForm.id ? 'Salvar alterações' : 'Cadastrar item'}
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        open={movementModalOpen}
        onClose={() => setMovementModalOpen(false)}
        size="max-w-3xl"
        showCloseButton={false}
        panelClassName="inventory-movement-modal-panel"
      >
        <form className="inventory-movement-form" onSubmit={handleSaveMovement}>
          <div className="inventory-modal-heading">
            <div className="inventory-modal-heading__identity">
              <span className={`inventory-modal-heading__icon inventory-modal-heading__icon--${movementTone}`}>
                <SelectedMovementIcon />
              </span>
              <div>
                <h3>Movimentação de estoque</h3>
                <p>Escolha o tipo, confira o saldo e registre a movimentação.</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setMovementModalOpen(false)}
              className="inventory-modal-close"
              aria-label="Fechar"
            >
              <FaTimes />
            </button>
          </div>

          <section className="inventory-form-section">
            <div className="inventory-form-section__heading">
              <span><FaExchangeAlt /></span>
              <div>
                <strong>Tipo de movimentação</strong>
                <small>A cor identifica rapidamente o impacto no estoque.</small>
              </div>
            </div>
            <div className="inventory-movement-types">
              {MOVEMENT_TYPES.map((type) => {
                const TypeIcon = type.icon;
                const tone =
                  type.value === 'INBOUND'
                    ? 'inbound'
                    : type.value === 'OUTBOUND'
                    ? 'outbound'
                    : 'transfer';
                const active = movementForm.type === type.value;
                return (
                  <button
                    key={type.value}
                    type="button"
                    className={`inventory-movement-type inventory-movement-type--${tone} ${active ? 'is-active' : ''}`}
                    onClick={() =>
                      setMovementForm((prev) => ({ ...prev, type: type.value }))
                    }
                  >
                    <span><TypeIcon /></span>
                    <div>
                      <strong>{type.label}</strong>
                      <small>
                        {type.value === 'INBOUND'
                          ? 'Adiciona saldo ao estoque'
                          : type.value === 'OUTBOUND'
                          ? 'Retira saldo do estoque'
                          : 'Move entre depósitos'}
                      </small>
                    </div>
                  </button>
                );
              })}
            </div>
          </section>

          <section className="inventory-form-section">
            <div className="inventory-form-section__heading">
              <span><FaBoxes /></span>
              <div>
                <strong>Item</strong>
                <small>Selecione o item e confira a situação atual antes de continuar.</small>
              </div>
            </div>
            <label className="inventory-field">
              <span>Item <b>*</b></span>
              <select
                required
                value={movementForm.itemId}
                onChange={(event) =>
                  setMovementForm((prev) => ({ ...prev, itemId: event.target.value }))
                }
              >
                <option value="">Selecione um item</option>
                {items.map((item) => (
                  <option key={item.id ?? item.sku} value={item.id ?? item.sku}>
                    {item.name}
                  </option>
                ))}
              </select>
            </label>

            {selectedMovementItem && (
              <div className="inventory-movement-summary">
                <div>
                  <span>Estoque atual</span>
                  <strong>{selectedMovementItem.stock} {selectedMovementItem.unit}</strong>
                </div>
                <div>
                  <span>Estoque mínimo</span>
                  <strong>{selectedMovementItem.minimumStock || 0} {selectedMovementItem.unit}</strong>
                </div>
                <div>
                  <span>Depósito principal</span>
                  <strong>{selectedMovementItem.warehouse?.name || 'Não definido'}</strong>
                </div>
                <span className={`inventory-stock-status inventory-stock-status--${selectedMovementStockStatus.tone}`}>
                  {selectedMovementStockStatus.label}
                </span>
              </div>
            )}

            {selectedMovementItem?.warehouses?.length ? (
              <div className="inventory-warehouse-stock-list">
                <span>Disponível por depósito</span>
                <div>
                  {selectedMovementItem.warehouses.map((entry) => (
                    <span key={`${selectedMovementItem.id}-${entry.id ?? entry.name}`}>
                      <FaWarehouse />
                      <b>{entry.name || 'Sem depósito'}</b>
                      {Number(entry.quantity || 0)} {selectedMovementItem.unit}
                    </span>
                  ))}
                </div>
              </div>
            ) : null}
          </section>

          <section className="inventory-form-section">
            <div className="inventory-form-section__heading">
              <span><FaWarehouse /></span>
              <div>
                <strong>Origem e destino</strong>
                <small>Os campos exibidos mudam de acordo com o tipo selecionado.</small>
              </div>
            </div>
            <div className="inventory-form-grid inventory-form-grid--two">
              {movementForm.type !== 'INBOUND' && (
                <label className="inventory-field">
                  <span>Depósito de origem <b>*</b></span>
                  <select
                    required
                    value={movementForm.originWarehouseId}
                    onChange={(event) =>
                      setMovementForm((prev) => ({
                        ...prev,
                        originWarehouseId: event.target.value,
                      }))
                    }
                  >
                    <option value="">Selecione</option>
                    {warehouses.map((warehouse) => (
                      <option
                        key={warehouse.id ?? warehouse.name}
                        value={warehouse.id ?? warehouse.name}
                      >
                        {warehouse.name}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              {movementForm.type !== 'OUTBOUND' && (
                <label className="inventory-field">
                  <span>Depósito de destino <b>*</b></span>
                  <select
                    required
                    value={movementForm.targetWarehouseId}
                    onChange={(event) =>
                      setMovementForm((prev) => ({
                        ...prev,
                        targetWarehouseId: event.target.value,
                      }))
                    }
                  >
                    <option value="">Selecione</option>
                    {warehouses.map((warehouse) => (
                      <option
                        key={warehouse.id ?? warehouse.name}
                        value={warehouse.id ?? warehouse.name}
                      >
                        {warehouse.name}
                      </option>
                    ))}
                  </select>
                </label>
              )}
            </div>
          </section>

          <section className="inventory-form-section">
            <div className="inventory-form-section__heading">
              <span><FaClipboardList /></span>
              <div>
                <strong>Dados da movimentação</strong>
                <small>Informe a quantidade e os dados que ajudam na rastreabilidade.</small>
              </div>
            </div>

            <div className="inventory-form-grid inventory-form-grid--two">
              <label className="inventory-field">
                <span>Quantidade <b>*</b></span>
                <div className="inventory-quantity-control">
                  <button
                    type="button"
                    aria-label="Diminuir quantidade"
                    onClick={() =>
                      setMovementForm((prev) => ({
                        ...prev,
                        quantity: String(
                          Math.max(0, Number.parseFloat(prev.quantity || '0') - 1),
                        ),
                      }))
                    }
                  >
                    −
                  </button>
                  <input
                    type="number"
                    min="0.01"
                    step="0.01"
                    required
                    value={movementForm.quantity}
                    onChange={(event) =>
                      setMovementForm((prev) => ({ ...prev, quantity: event.target.value }))
                    }
                    placeholder="0"
                  />
                  <span>{selectedMovementItem?.unit || 'un'}</span>
                  <button
                    type="button"
                    aria-label="Aumentar quantidade"
                    onClick={() =>
                      setMovementForm((prev) => ({
                        ...prev,
                        quantity: String(Number.parseFloat(prev.quantity || '0') + 1),
                      }))
                    }
                  >
                    +
                  </button>
                </div>
              </label>

              <label className="inventory-field">
                <span>Custo unitário (R$)</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={movementForm.unitCost}
                  onChange={(event) =>
                    setMovementForm((prev) => ({ ...prev, unitCost: event.target.value }))
                  }
                  placeholder="0,00"
                />
              </label>

              <label className="inventory-field">
                <span>Data da movimentação</span>
                <input
                  type="datetime-local"
                  value={movementForm.occurredAt}
                  onChange={(event) =>
                    setMovementForm((prev) => ({ ...prev, occurredAt: event.target.value }))
                  }
                />
              </label>

              <label className="inventory-field">
                <span>Referência</span>
                <input
                  type="text"
                  value={movementForm.referenceCode}
                  onChange={(event) =>
                    setMovementForm((prev) => ({
                      ...prev,
                      referenceCode: event.target.value,
                    }))
                  }
                  placeholder="NF, pedido, requisição..."
                />
              </label>
            </div>

            <label className="inventory-field inventory-field--notes">
              <span>Observações</span>
              <textarea
                value={movementForm.notes}
                onChange={(event) =>
                  setMovementForm((prev) => ({ ...prev, notes: event.target.value }))
                }
                rows={3}
                placeholder="Motivo, setor solicitante, observações adicionais..."
              />
            </label>
          </section>

          <div className={`inventory-movement-confirmation inventory-movement-confirmation--${movementTone}`}>
            <SelectedMovementIcon />
            <span>
              <strong>{selectedMovementType?.label || 'Movimentação'}</strong>
              {movementForm.quantity
                ? ` de ${movementForm.quantity} ${selectedMovementItem?.unit || 'un'}`
                : ' — informe a quantidade para concluir.'}
            </span>
          </div>

          <div className="inventory-modal-actions">
            <button
              type="button"
              onClick={() => setMovementModalOpen(false)}
              className="inventory-secondary-button"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={savingMovement}
              className={`inventory-movement-submit inventory-movement-submit--${movementTone}`}
            >
              {savingMovement ? <FaSpinner className="animate-spin" /> : <SelectedMovementIcon />}
              {savingMovement
                ? 'Registrando...'
                : movementForm.type === 'INBOUND'
                ? 'Registrar entrada'
                : movementForm.type === 'OUTBOUND'
                ? 'Registrar saída'
                : 'Registrar transferência'}
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        open={warehouseModalOpen}
        onClose={() => setWarehouseModalOpen(false)}
        size="max-w-xl"
        showCloseButton={false}
      >
        <form className="space-y-6" onSubmit={handleSaveWarehouse}>
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-slate-800">
              {warehouseForm.id ? 'Editar depósito' : 'Novo depósito'}
            </h3>
            <button
              type="button"
              onClick={() => setWarehouseModalOpen(false)}
              className="text-slate-400 transition hover:text-slate-600"
              aria-label="Fechar"
            >
              <FaTimes />
            </button>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <label className="flex flex-col gap-1 text-sm text-slate-600">
              Nome do depósito
              <input
                type="text"
                required
                value={warehouseForm.name}
                onChange={(event) =>
                  setWarehouseForm((prev) => ({ ...prev, name: event.target.value }))
                }
                className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-300 focus:outline-none"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-slate-600">
              Código interno
              <input
                type="text"
                value={warehouseForm.code}
                onChange={(event) =>
                  setWarehouseForm((prev) => ({ ...prev, code: event.target.value }))
                }
                className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-300 focus:outline-none"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-slate-600">
              Responsável
              <input
                type="text"
                value={warehouseForm.manager}
                onChange={(event) =>
                  setWarehouseForm((prev) => ({ ...prev, manager: event.target.value }))
                }
                className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-300 focus:outline-none"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-slate-600">
              Telefone
              <input
                type="tel"
                value={warehouseForm.phone}
                onChange={(event) =>
                  setWarehouseForm((prev) => ({ ...prev, phone: event.target.value }))
                }
                className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-300 focus:outline-none"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-slate-600">
              E-mail
              <input
                type="email"
                value={warehouseForm.email}
                onChange={(event) =>
                  setWarehouseForm((prev) => ({ ...prev, email: event.target.value }))
                }
                className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-300 focus:outline-none"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-slate-600 md:col-span-2">
              Endereço
              <input
                type="text"
                value={warehouseForm.address}
                onChange={(event) =>
                  setWarehouseForm((prev) => ({ ...prev, address: event.target.value }))
                }
                className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-300 focus:outline-none"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-slate-600 md:col-span-2">
              Observações
              <textarea
                rows={3}
                value={warehouseForm.notes}
                onChange={(event) =>
                  setWarehouseForm((prev) => ({ ...prev, notes: event.target.value }))
                }
                className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-300 focus:outline-none"
              />
            </label>
          </div>

          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={() => setWarehouseModalOpen(false)}
              className="rounded-full border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 transition hover:border-slate-300"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={savingWarehouse}
              className="inline-flex items-center gap-2 rounded-full bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow transition hover:bg-indigo-500 disabled:cursor-not-allowed disabled:bg-indigo-300"
            >
              {savingWarehouse ? 'Salvando...' : 'Salvar depósito'}
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        open={categoryModalOpen}
        onClose={() => setCategoryModalOpen(false)}
        size="max-w-md"
        showCloseButton={false}
      >
        <form className="space-y-6" onSubmit={handleSaveCategory}>
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-slate-800">
              {categoryForm.id ? 'Editar categoria' : 'Nova categoria'}
            </h3>
            <button
              type="button"
              onClick={() => setCategoryModalOpen(false)}
              className="text-slate-400 transition hover:text-slate-600"
              aria-label="Fechar"
            >
              <FaTimes />
            </button>
          </div>

          <label className="flex flex-col gap-1 text-sm text-slate-600">
            Nome da categoria
            <input
              type="text"
              required
              value={categoryForm.name}
              onChange={(event) =>
                setCategoryForm((prev) => ({ ...prev, name: event.target.value }))
              }
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-300 focus:outline-none"
            />
          </label>

          <label className="flex flex-col gap-1 text-sm text-slate-600">
            Descrição
            <textarea
              rows={3}
              value={categoryForm.description}
              onChange={(event) =>
                setCategoryForm((prev) => ({ ...prev, description: event.target.value }))
              }
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-300 focus:outline-none"
            />
          </label>

          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={() => setCategoryModalOpen(false)}
              className="rounded-full border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 transition hover:border-slate-300"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={savingCategory}
              className="inline-flex items-center gap-2 rounded-full bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow transition hover:bg-indigo-500 disabled:cursor-not-allowed disabled:bg-indigo-300"
            >
              {savingCategory ? 'Salvando...' : 'Salvar categoria'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
